import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Megaphone, 
  MessageCircle, 
  FileText, 
  Plus, 
  Send,
  X,
  CheckCircle,
  Clock,
  AlertCircle,
  Image as ImageIcon,
  Upload,
  UserCheck,
  Bus,
  Activity,
  AlertTriangle,
  User,
  Edit,
  Lock,
  GraduationCap,
  BookOpen,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  getBatchById, 
  checkBatchEnrollment, 
  getTests, 
  getTestById, 
  getTimetables, 
  getStudentDocuments, 
  COLLECTIONS, 
  getCertificates, 
  getReportCards, 
  getStudentFees, 
  getFeeReceiptByPaymentId, 
  getBatchAnnouncements, 
  getBatchAssignments, 
  getStudentSubmissions, 
  getUserProfile,
  getBatchSyllabus,
  saveBatchSyllabus,
  getMasterTimetable
} from '../../firebase/collections';
import { collection, addDoc, query, where, orderBy, limit, onSnapshot, serverTimestamp, doc, updateDoc, getDocs, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadFile, getFileDownloadURL } from '../../firebase/r2storage';
import BatchSchedule from '../../components/batch/BatchSchedule';
import BatchChat from '../../components/batch/BatchChat';
import BatchDirectMessages from '../../components/batch/BatchDirectMessages';
import LeaveManagement from '../../components/batch/LeaveManagement';
import { Calendar, Award, Download, Loader2, Mail, Receipt, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';

// Batch Assignments Tab Component
const BatchAssignmentsTab = ({ batchId, currentUser }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState({});
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submissionText, setSubmissionText] = useState('');
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [uploadingSubmission, setUploadingSubmission] = useState(false);

  // OPTIMIZED: Load once and refresh periodically
  useEffect(() => {
    if (batchId) {
      loadAssignments();
      loadSubmissions();
      
      // Refresh every 5 minutes instead of real-time listener
      const refreshInterval = setInterval(() => {
        loadAssignments();
        loadSubmissions();
      }, 300000); // 5 minutes
      
      return () => clearInterval(refreshInterval);
    }
  }, [batchId]);

  // OPTIMIZED: Use getDocs instead of onSnapshot + cache + pagination
  const loadAssignments = async () => {
    try {
      // Check cache first (5 minute TTL)
      const cacheKey = `assignments_${batchId}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        setAssignments(JSON.parse(cached));
        setLoading(false);
        return;
      }

      // Use the new getBatchAssignments function with URL conversion
      const result = await getBatchAssignments(batchId);
      
      if (result.success) {
        setAssignments(result.data);
        
        // Cache the results
        localStorage.setItem(cacheKey, JSON.stringify(result.data));
        localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
      } else {
        console.error('Error loading assignments:', result.error);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading assignments:', error);
      setLoading(false);
    }
  };

  // OPTIMIZED: Use getDocs instead of onSnapshot + cache
  const loadSubmissions = async () => {
    if (!currentUser?.uid) return;
    
    try {
      // Check cache first (5 minute TTL)
      const cacheKey = `submissions_${batchId}_${currentUser.uid}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        setSubmissions(JSON.parse(cached));
        return;
      }

      // Use the new getStudentSubmissions function with URL conversion
      const result = await getStudentSubmissions(batchId, currentUser.uid);
      
      if (result.success) {
        setSubmissions(result.data);
        
        // Cache the results
        localStorage.setItem(cacheKey, JSON.stringify(result.data));
        localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
      } else {
        console.error('Error loading submissions:', result.error);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingSubmission(true);
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        const fileName = `assignment-submissions/${batchId}/${selectedAssignment.id}/${currentUser.uid}/${Date.now()}_${file.name}`;
        const result = await uploadFile(file, fileName);
        
        if (result.success) {
          uploadedFiles.push({
            name: file.name,
            url: result.url,
            type: file.type,
            size: file.size
          });
        }
      }
      
      setSubmissionFiles(prev => [...prev, ...uploadedFiles]);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploadingSubmission(false);
    }
  };

  const handleSubmitAssignment = async () => {
    if (!submissionText.trim() && submissionFiles.length === 0) {
      toast.error('Please provide either text or file submission');
      return;
    }

    try {
      const submissionData = {
        assignmentId: selectedAssignment.id,
        batchId,
        studentId: currentUser.uid,
        studentName: currentUser.displayName || currentUser.email,
        studentEmail: currentUser.email,
        submissionText: submissionText.trim(),
        submissionFiles,
        submittedAt: serverTimestamp(),
        status: 'submitted'
      };

      // Check if submission already exists
      const existingSubmission = submissions[selectedAssignment.id];
      if (existingSubmission) {
        // Update existing submission
        await updateDoc(doc(db, 'assignmentSubmissions', existingSubmission.id), submissionData);
        toast.success('Assignment updated successfully');
      } else {
        // Create new submission
        await addDoc(collection(db, 'assignmentSubmissions'), submissionData);
        toast.success('Assignment submitted successfully');
      }

      setShowSubmissionModal(false);
      setSelectedAssignment(null);
      setSubmissionText('');
      setSubmissionFiles([]);
    } catch (error) {
      console.error('Error submitting assignment:', error);
      toast.error('Failed to submit assignment');
    }
  };

  const openSubmissionModal = (assignment) => {
    setSelectedAssignment(assignment);
    const existingSubmission = submissions[assignment.id];
    if (existingSubmission) {
      setSubmissionText(existingSubmission.submissionText || '');
      setSubmissionFiles(existingSubmission.submissionFiles || []);
    } else {
      setSubmissionText('');
      setSubmissionFiles([]);
    }
    setShowSubmissionModal(true);
  };

  const isOverdue = (dueDate) => {
    return new Date() > new Date(dueDate);
  };

  const getStatusColor = (assignment) => {
    const submission = submissions[assignment.id];
    if (submission) {
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    }
    if (isOverdue(assignment.dueDate)) {
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    }
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  };

  const getStatusText = (assignment) => {
    const submission = submissions[assignment.id];
    if (submission) {
      return 'Submitted';
    }
    if (isOverdue(assignment.dueDate)) {
      return 'Overdue';
    }
    return 'Pending';
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <FileText className="w-6 h-6 mr-2 text-blue-600" />
          Assignments
        </h2>
      </div>



      {assignments.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No assignments available yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {assignments.map((assignment) => {
            const submission = submissions[assignment.id];
            const overdue = isOverdue(assignment.dueDate);
            
            return (
              <div key={assignment.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {assignment.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      {assignment.description}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        Due: {new Date(assignment.dueDate).toLocaleDateString()}
                      </span>
                      <span>Max Marks: {assignment.maxMarks}</span>
                    </div>
                    {assignment.instructions && (
                      <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          <strong>Instructions:</strong> {assignment.instructions}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(assignment)}`}>
                      {getStatusText(assignment)}
                    </span>
                    {!submission && !overdue && (
                      <button
                        onClick={() => openSubmissionModal(assignment)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                      >
                        Submit Assignment
                      </button>
                    )}
                    {submission && (
                      <button
                        onClick={() => openSubmissionModal(assignment)}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
                      >
                        View Submission
                      </button>
                    )}
                  </div>
                </div>

                {submission && (
                  <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-500">
                    <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">Your Submission:</h4>
                    {submission.submissionText && (
                      <p className="text-gray-700 dark:text-gray-300 mb-2">{submission.submissionText}</p>
                    )}
                    {submission.submissionFiles && submission.submissionFiles.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Attached Files:</p>
                        {submission.submissionFiles.map((file, index) => (
                          <a
                            key={index}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            📎 {file.name}
                          </a>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Submitted on: {submission.submittedAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Submission Modal */}
      {showSubmissionModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {submissions[selectedAssignment.id] ? 'View Submission' : 'Submit Assignment'}
                </h3>
                <button
                  onClick={() => {
                    setShowSubmissionModal(false);
                    setSelectedAssignment(null);
                    setSubmissionText('');
                    setSubmissionFiles([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{selectedAssignment.title}</h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">{selectedAssignment.description}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                  <span>Due: {new Date(selectedAssignment.dueDate).toLocaleDateString()}</span>
                  <span>Max Marks: {selectedAssignment.maxMarks}</span>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Text Submission
                  </label>
                  <textarea
                    value={submissionText}
                    onChange={(e) => setSubmissionText(e.target.value)}
                    placeholder="Enter your assignment submission here..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    rows="6"
                    disabled={submissions[selectedAssignment.id] && !isOverdue(selectedAssignment.dueDate)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    File Attachments
                  </label>
                  {!submissions[selectedAssignment.id] || isOverdue(selectedAssignment.dueDate) ? (
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      disabled={uploadingSubmission}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                    />
                  ) : null}
                  
                  {submissionFiles.length > 0 && (
                    <div className="mt-2 space-y-2">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Attached Files:</p>
                      {submissionFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{file.name}</span>
                          {!submissions[selectedAssignment.id] && (
                            <button
                              onClick={() => setSubmissionFiles(prev => prev.filter((_, i) => i !== index))}
                              className="text-red-600 hover:text-red-800 dark:text-red-400"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {!submissions[selectedAssignment.id] && !isOverdue(selectedAssignment.dueDate) && (
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowSubmissionModal(false);
                      setSelectedAssignment(null);
                      setSubmissionText('');
                      setSubmissionFiles([]);
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitAssignment}
                    disabled={uploadingSubmission}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
                  >
                    {uploadingSubmission ? 'Uploading...' : 'Submit Assignment'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Batch Attendance Tab Component
const BatchAttendanceTab = ({ batchId, currentUser, isTeacherOrAdmin }) => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [attendanceStatus, setAttendanceStatus] = useState('present'); // present, absent, late
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [canMarkAttendance, setCanMarkAttendance] = useState(false);
  const [capturedFace, setCapturedFace] = useState(null);
  const [videoStream, setVideoStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [leaveRecords, setLeaveRecords] = useState([]);

  const checkMarkingPermission = useCallback(async () => {
    if (!batchId || isTeacherOrAdmin) {
      setCanMarkAttendance(true);
      return;
    }
    
    try {
      // Check if admin has enabled attendance marking for today
      const today = new Date().toISOString().split('T')[0];
      const q = query(
        collection(db, 'attendanceSettings'),
        where('batchId', '==', batchId),
        where('date', '==', today),
        where('allowStudentMarking', '==', true)
      );
      const snapshot = await getDocs(q);
      setCanMarkAttendance(!snapshot.empty);
    } catch (error) {
      setCanMarkAttendance(false);
    }
  }, [batchId, isTeacherOrAdmin]);

  // OPTIMIZED: Load attendance with getDocs + cache + current month only
  const loadAttendance = useCallback(async (forceRefresh = false) => {
    if (!batchId || !currentUser?.uid) {
      console.log('⚠️ Cannot load attendance: missing batchId or currentUser.uid', { batchId, uid: currentUser?.uid });
      return;
    }
    
    try {
      // CRITICAL FIX: Use currentUser.uid as studentId (matches userId from batchEnrollments)
      const studentId = currentUser.uid;
      
      console.log('📋 Loading attendance for:', {
        batchId,
        studentId,
        email: currentUser.email,
        forceRefresh
      });
      
      // Check cache first (5 minute TTL for attendance - reduced for faster updates)
      const cacheKey = `attendance_${batchId}_${studentId}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      // Use cache only if not forcing refresh and cache is valid (5 minutes)
      if (!forceRefresh && cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        const cachedRecords = JSON.parse(cached);
        console.log(`📦 Using cached attendance (${cachedRecords.length} records)`);
        setAttendanceRecords(cachedRecords);
        setLoading(false);
        return;
      }

      console.log('🔍 Querying Firestore for attendance...');
      
      // Load attendance for this specific student only
      // CRITICAL: Query by studentId which matches userId from enrollment
      const q = query(
        collection(db, 'batchAttendance'),
        where('batchId', '==', batchId),
        where('studentId', '==', studentId),
        limit(100)
      );
      
      const snapshot = await getDocs(q);
      const records = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        records.push({ id: doc.id, ...data });
        console.log(`   📅 Found attendance: ${data.date} - ${data.status}`);
      });
      
      console.log(`✅ Loaded ${records.length} attendance records for student ${studentId} in batch ${batchId}`);
      
      if (records.length === 0) {
        console.warn('⚠️ No attendance records found. This could mean:');
        console.warn('   1. Student has no attendance marked yet');
        console.warn('   2. studentId in attendance does not match currentUser.uid');
        console.warn('   3. batchId does not match');
      }
      
      // DON'T filter by month here - load ALL records so they're available when switching months
      // The calendar will filter by the displayed month
      
      // Sort client-side by date descending
      records.sort((a, b) => {
        const dateA = a.date || '';
        const dateB = b.date || '';
        return dateB.localeCompare(dateA);
      });
      
      setAttendanceRecords(records);
      
      // Cache ALL the results (not filtered by month)
      localStorage.setItem(cacheKey, JSON.stringify(records));
      localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Error loading attendance:', error);
      setLoading(false);
    }
  }, [batchId, currentUser?.uid, currentUser?.email]);

  // OPTIMIZED: Load leave records with getDocs + cache
  const loadLeaveRecords = useCallback(async () => {
    if (!batchId || !currentUser?.uid) return;
    
    try {
      // Check cache first (30 minute TTL)
      const cacheKey = `leave_${batchId}_${currentUser.uid}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 1800000) {
        setLeaveRecords(JSON.parse(cached));
        return;
      }

      const q = query(
        collection(db, 'leaveApplications'),
        where('batchId', '==', batchId),
        where('studentId', '==', currentUser.uid),
        where('status', '==', 'approved'),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      const records = [];
      snapshot.forEach((doc) => {
        records.push({ id: doc.id, ...doc.data() });
      });
      setLeaveRecords(records);
      
      // Cache the results
      localStorage.setItem(cacheKey, JSON.stringify(records));
      localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
    } catch (error) {
      console.error('Error loading leave records:', error);
    }
  }, [batchId, currentUser?.uid]);

  // OPTIMIZED: Load once and refresh periodically
  useEffect(() => {
    if (!batchId || !currentUser?.uid) return;
    
    loadAttendance();
    loadLeaveRecords();
    checkMarkingPermission();
    
    // Refresh every 2 minutes to catch teacher-marked attendance faster
    const refreshInterval = setInterval(() => {
      loadAttendance(true); // Force refresh to bypass cache
      loadLeaveRecords();
      checkMarkingPermission();
    }, 120000); // 2 minutes
    
    return () => clearInterval(refreshInterval);
  }, [batchId, currentUser?.uid, loadAttendance, loadLeaveRecords, checkMarkingPermission]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 640, height: 480, facingMode: 'user' } 
      });
      setVideoStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast.error('Failed to access camera');
    }
  };

  const captureFace = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      setCapturedFace(blob);
      // Stop camera
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
        setVideoStream(null);
      }
    }, 'image/jpeg', 0.8);
  };

  const handleMarkAttendance = async () => {
    const today = new Date().toISOString().split('T')[0];
    
    if (!canMarkAttendance && !isTeacherOrAdmin) {
      toast.error('Attendance marking is not enabled for today');
      return;
    }

    if (!capturedFace && !isTeacherOrAdmin) {
      toast.error('Please capture your face');
      return;
    }

    setMarkingAttendance(true);
    try {
      // CRITICAL FIX: Ensure we're using the correct studentId (which is currentUser.uid)
      const studentId = currentUser.uid;
      
      // Check if attendance already marked for today
      const existingQuery = query(
        collection(db, 'batchAttendance'),
        where('batchId', '==', batchId),
        where('studentId', '==', studentId),
        where('date', '==', today)
      );
      const existing = await getDocs(existingQuery);
      
      if (!existing.empty) {
        toast.error('Attendance already marked for today');
        setMarkingAttendance(false);
        return;
      }

      let faceImageUrl = null;
      if (capturedFace) {
        // Upload face image
        const faceFileName = `attendance_faces/${batchId}/${studentId}_${today}_${Date.now()}.jpg`;
        const result = await uploadFile(capturedFace, faceFileName);
        if (result.success) {
          faceImageUrl = result.url;
        }
      }

      // Create attendance record with correct studentId
      await addDoc(collection(db, 'batchAttendance'), {
        batchId,
        studentId: studentId, // This matches the userId from batchEnrollments
        studentName: currentUser.displayName || currentUser.email,
        date: today,
        status: attendanceStatus,
        faceImageUrl: faceImageUrl,
        markedAt: serverTimestamp(),
        markedBy: studentId,
        isSelfMarked: !isTeacherOrAdmin
      });
      
      console.log(`✅ Attendance marked for student ${studentId} on ${today}: ${attendanceStatus}`);
      
      // Clear cache to force reload of fresh data
      const cacheKey = `attendance_${batchId}_${studentId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}_ts`);
      
      // Reload attendance immediately
      await loadAttendance();
      
      toast.success('Attendance marked successfully');
      setShowMarkModal(false);
      setCapturedFace(null);
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
        setVideoStream(null);
      }
    } catch (error) {
      console.error('❌ Error marking attendance:', error);
      toast.error('Failed to mark attendance');
    } finally {
      setMarkingAttendance(false);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const isSunday = (date) => {
    return date.getDay() === 0;
  };

  const isLeaveDay = (dateStr) => {
    return leaveRecords.some(leave => {
      const leaveStart = new Date(leave.startDate);
      const leaveEnd = new Date(leave.endDate);
      const checkDate = new Date(dateStr);
      return checkDate >= leaveStart && checkDate <= leaveEnd;
    });
  };

  const getAttendanceForDate = (dateStr) => {
    return attendanceRecords.find(r => r.date === dateStr);
  };

  const calculateMonthStats = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(currentMonth);
    const today = new Date();
    
    let present = 0;
    let absent = 0;
    let leave = 0;
    let holidays = 0;
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      // Create date string without timezone conversion
      const dateYear = date.getFullYear();
      const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
      const dateDay = String(day).padStart(2, '0');
      const dateStr = `${dateYear}-${dateMonth}-${dateDay}`;
      
      const isPast = date < today && date.toDateString() !== today.toDateString();
      
      if (isSunday(date)) {
        holidays++;
      } else if (isLeaveDay(dateStr)) {
        leave++;
      } else {
        const attendance = getAttendanceForDate(dateStr);
        if (attendance) {
          if (attendance.status === 'present' || attendance.status === 'late') {
            present++;
          } else if (attendance.status === 'absent') {
            absent++;
          }
        } else if (isPast) {
          // ONLY auto-mark as absent if it's in the current month or past months
          // Don't mark future months as absent
          const isCurrentOrPastMonth = (dateYear < today.getFullYear()) || 
                                       (dateYear === today.getFullYear() && parseInt(dateMonth) <= (today.getMonth() + 1));
          if (isCurrentOrPastMonth) {
            absent++;
          }
        }
      }
    }
    
    return { present, absent, leave, holidays };
  };

  const calculateOverallStats = () => {
    const today = new Date();
    let present = 0;
    let absent = 0;
    
    // Count from attendance records
    attendanceRecords.forEach(record => {
      if (record.status === 'present' || record.status === 'late') {
        present++;
      } else if (record.status === 'absent') {
        absent++;
      }
    });
    
    // Count auto-absent days (past dates without records, excluding holidays and leaves)
    // ONLY for the current displayed month
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(currentMonth);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateYear = date.getFullYear();
      const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
      const dateDay = String(day).padStart(2, '0');
      const dateStr = `${dateYear}-${dateMonth}-${dateDay}`;
      
      const isPast = date < today && date.toDateString() !== today.toDateString();
      const hasAttendance = getAttendanceForDate(dateStr);
      const isHoliday = isSunday(date);
      const isLeave = isLeaveDay(dateStr);
      
      // ONLY auto-mark as absent if it's in current month or past months
      const isCurrentOrPastMonth = (dateYear < today.getFullYear()) || 
                                   (dateYear === today.getFullYear() && parseInt(dateMonth) <= (today.getMonth() + 1));
      
      // If it's a past date without attendance, and not a holiday or leave, count as absent
      if (isPast && !hasAttendance && !isHoliday && !isLeave && isCurrentOrPastMonth) {
        absent++;
      }
    }
    
    const totalMeetings = present + absent;
    const presentPercentage = totalMeetings > 0 ? ((present / totalMeetings) * 100).toFixed(2) : 0;
    const absentPercentage = totalMeetings > 0 ? ((absent / totalMeetings) * 100).toFixed(2) : 0;
    
    return { totalMeetings, present, absent, presentPercentage, absentPercentage };
  };

  const monthStats = calculateMonthStats();
  const overallStats = calculateOverallStats();

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-2 max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center">
          <UserCheck className="w-4 h-4 mr-1.5 text-green-600" />
          Attendance
        </h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              toast.loading('Refreshing attendance...', { id: 'refresh-attendance' });
              loadAttendance(true).then(() => {
                toast.success('Attendance refreshed!', { id: 'refresh-attendance' });
              }).catch(() => {
                toast.error('Failed to refresh', { id: 'refresh-attendance' });
              });
            }}
            className="flex items-center space-x-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
            title="Refresh attendance"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Refresh</span>
          </button>
          {(canMarkAttendance || isTeacherOrAdmin) && (
            <button
              onClick={() => {
                setShowMarkModal(true);
                if (!isTeacherOrAdmin) {
                  startCamera();
                }
              }}
              className="flex items-center space-x-1 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs"
            >
              <Plus className="w-3 h-3" />
              <span>Mark</span>
            </button>
          )}
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between mb-2 bg-gray-50 dark:bg-gray-700 rounded p-1.5">
        <button onClick={previousMonth} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
          <ArrowLeft className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" />
        </button>
        <h3 className="text-xs font-semibold text-gray-900 dark:text-white">
          {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
        </h3>
        <button onClick={nextMonth} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
          <ArrowLeft className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300 rotate-180" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="mb-2">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-0.5 mb-1">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
            <div key={idx} className="text-center text-[10px] font-medium text-gray-500 dark:text-gray-400 py-0.5">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-0.5">
          {Array.from({ length: getFirstDayOfMonth(currentMonth) }).map((_, index) => (
            <div key={`empty-${index}`} className="aspect-square" />
          ))}

          {Array.from({ length: getDaysInMonth(currentMonth) }).map((_, index) => {
            const day = index + 1;
            // Create date string without timezone conversion
            const year = currentMonth.getFullYear();
            const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
            const dayStr = String(day).padStart(2, '0');
            const dateStr = `${year}-${month}-${dayStr}`;
            
            const date = new Date(year, currentMonth.getMonth(), day);
            const attendance = getAttendanceForDate(dateStr);
            const isHoliday = isSunday(date);
            const isLeave = isLeaveDay(dateStr);
            const isToday = new Date().toDateString() === date.toDateString();
            const isPast = date < new Date() && !isToday;

            let bgColor = 'bg-white dark:bg-gray-700';
            let borderColor = 'border-gray-200 dark:border-gray-600';
            let textColor = 'text-gray-900 dark:text-white';

            if (isHoliday) {
              // Sundays - gray background
              bgColor = 'bg-gray-100 dark:bg-gray-800';
              textColor = 'text-gray-400 dark:text-gray-500';
            } else if (isLeave) {
              // Leave days - cyan background
              bgColor = 'bg-cyan-100 dark:bg-cyan-900/30';
              textColor = 'text-cyan-900 dark:text-cyan-100';
            } else if (attendance) {
              // Has attendance record
              if (attendance.status === 'present' || attendance.status === 'late') {
                bgColor = 'bg-green-100 dark:bg-green-900/30';
                textColor = 'text-green-900 dark:text-green-100';
              } else if (attendance.status === 'absent') {
                bgColor = 'bg-red-100 dark:bg-red-900/30';
                textColor = 'text-red-900 dark:text-red-100';
              }
            } else if (isPast) {
              // Past date without attendance - mark as absent
              bgColor = 'bg-red-100 dark:bg-red-900/30';
              textColor = 'text-red-900 dark:text-red-100';
            }

            if (isToday) {
              borderColor = 'border-blue-500 border-2';
            }

            return (
              <div
                key={day}
                className={`aspect-square border ${borderColor} ${bgColor} rounded flex items-center justify-center`}
              >
                <span className={`text-[10px] font-medium ${textColor}`}>{day}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Month Summary */}
      <div className="grid grid-cols-4 gap-1.5 mb-2">
        <div className="bg-green-50 dark:bg-green-900/20 rounded p-1.5 text-center border border-green-200 dark:border-green-800">
          <Calendar className="w-3 h-3 text-green-600 mx-auto mb-0.5" />
          <p className="text-sm font-bold text-green-600">{monthStats.present}</p>
          <p className="text-[9px] text-gray-600 dark:text-gray-400">Present</p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded p-1.5 text-center border border-red-200 dark:border-red-800">
          <X className="w-3 h-3 text-red-600 mx-auto mb-0.5" />
          <p className="text-sm font-bold text-red-600">{monthStats.absent}</p>
          <p className="text-[9px] text-gray-600 dark:text-gray-400">Absent</p>
        </div>
        <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded p-1.5 text-center border border-cyan-200 dark:border-cyan-800">
          <Calendar className="w-3 h-3 text-cyan-600 mx-auto mb-0.5" />
          <p className="text-sm font-bold text-cyan-600">{monthStats.leave}</p>
          <p className="text-[9px] text-gray-600 dark:text-gray-400">Leave</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 rounded p-1.5 text-center border border-gray-200 dark:border-gray-600">
          <Calendar className="w-3 h-3 text-gray-600 mx-auto mb-0.5" />
          <p className="text-sm font-bold text-gray-600">{monthStats.holidays}</p>
          <p className="text-[9px] text-gray-600 dark:text-gray-400">Holidays</p>
        </div>
      </div>

      {/* Overall Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded p-2 border border-blue-200 dark:border-blue-800">
        <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-1.5">Overall Summary</h3>
        
        <div className="flex items-center justify-between mb-1.5 text-[10px]">
          <span className="text-gray-600 dark:text-gray-400">Meetings: <span className="font-bold text-gray-900 dark:text-white">{overallStats.totalMeetings}</span></span>
          <div className="flex gap-2">
            <span className="text-green-600 dark:text-green-400 font-medium">P: {overallStats.present}</span>
            <span className="text-red-600 dark:text-red-400 font-medium">A: {overallStats.absent}</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <div>
            <div className="flex justify-between text-[9px] mb-0.5">
              <span className="text-green-700 dark:text-green-400 font-medium">Present {overallStats.presentPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${overallStats.presentPercentage}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-[9px] mb-0.5">
              <span className="text-red-700 dark:text-red-400 font-medium">Absent {overallStats.absentPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div className="bg-red-500 h-1.5 rounded-full" style={{ width: `${overallStats.absentPercentage}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Mark Attendance Modal */}
      {showMarkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Mark Attendance</h3>
              <button onClick={() => {
                setShowMarkModal(false);
                if (videoStream) {
                  videoStream.getTracks().forEach(track => track.stop());
                  setVideoStream(null);
                }
                setCapturedFace(null);
              }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date</label>
                <input
                  type="date"
                  value={new Date().toISOString().split('T')[0]}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white bg-gray-100 dark:bg-gray-700"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Only today's attendance can be marked</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                <select
                  value={attendanceStatus}
                  onChange={(e) => setAttendanceStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="present">Present</option>
                  <option value="late">Late</option>
                  <option value="absent">Absent</option>
                </select>
              </div>
              
              {/* Face Capture Section - Only for students */}
              {!isTeacherOrAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Capture Face *
                  </label>
                  {!capturedFace ? (
                    <div className="space-y-2">
                      {videoStream && (
                        <div className="relative">
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600"
                          />
                          <canvas ref={canvasRef} className="hidden" />
                        </div>
                      )}
                      <button
                        onClick={captureFace}
                        disabled={!videoStream}
                        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {videoStream ? 'Capture Face' : 'Starting Camera...'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <img 
                        src={URL.createObjectURL(capturedFace)} 
                        alt="Captured face" 
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600"
                      />
                      <button
                        onClick={() => {
                          setCapturedFace(null);
                          startCamera();
                        }}
                        className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
                      >
                        Retake
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowMarkModal(false);
                    if (videoStream) {
                      videoStream.getTracks().forEach(track => track.stop());
                      setVideoStream(null);
                    }
                    setCapturedFace(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkAttendance}
                  disabled={markingAttendance || (!isTeacherOrAdmin && !capturedFace)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50"
                >
                  {markingAttendance ? 'Marking...' : 'Mark Attendance'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Batch Transport Tab Component
const BatchTransportTab = ({ batchId, currentUser, isTeacherOrAdmin }) => {
  const [transportInfo, setTransportInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState({
    queryAttempts: [],
    assignmentsFound: 0,
    studentTransportFound: 0,
    lastError: null
  });
  const queriesInitiatedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (queriesInitiatedRef.current) {
      return;
    }
    
    console.log('=== BatchTransportTab Debug ===');
    console.log('Batch ID:', batchId);
    console.log('Current User:', currentUser);
    console.log('User UID:', currentUser?.uid);
    console.log('User Email:', currentUser?.email);
    
    setDebugInfo(prev => ({
      ...prev,
      queryAttempts: [...prev.queryAttempts, 'Initializing...']
    }));
    
    if (!batchId || !currentUser?.uid) {
      console.log('Missing batchId or currentUser.uid');
      setDebugInfo(prev => ({
        ...prev,
        lastError: 'Missing batchId or currentUser.uid'
      }));
      return;
    }
    
    let unsubscribe = null;
    
    // First, try to find by email (one-time query)
    const tryEmailQuery = async () => {
      if (!currentUser.email) return false;
      
      console.log('Trying email query for studentTransport...');
      try {
        const emailQuery = query(
          collection(db, 'studentTransport'),
          where('batchId', '==', batchId),
          where('studentEmail', '==', currentUser.email)
        );
        const emailSnapshot = await getDocs(emailQuery);
        
        if (!emailSnapshot.empty) {
          console.log('Found transport by email');
          const transportData = emailSnapshot.docs[0].data();
          const transportId = emailSnapshot.docs[0].id;
          setTransportInfo({ id: transportId, ...transportData });
          setLoading(false);
          return true;
        }
      } catch (emailError) {
        console.error('Error querying by email:', emailError);
      }
      return false;
    };
    
    // Try the composite query first (requires index)
    const tryCompositeQuery = () => {
    console.log('Trying composite query for studentTransport...');
    setDebugInfo(prev => ({
      ...prev,
      queryAttempts: [...prev.queryAttempts, 'Trying composite query (batchId + studentId)']
    }));
    const q = query(
      collection(db, 'studentTransport'),
      where('batchId', '==', batchId),
      where('studentId', '==', currentUser.uid)
    );
    
      return onSnapshot(q, async (snapshot) => {
        console.log('Composite query snapshot received:', snapshot.size, 'documents');
        setDebugInfo(prev => ({
          ...prev,
          studentTransportFound: snapshot.size,
          queryAttempts: [...prev.queryAttempts, `Composite query returned ${snapshot.size} documents`]
        }));
        setError(null); // Clear any previous errors
      if (!snapshot.empty) {
        console.log('Found documents in studentTransport');
        // Filter for active records
        const activeRecords = snapshot.docs.filter(doc => {
          const data = doc.data();
          console.log('Document data:', data);
            return data.isActive !== false && data.batchId === batchId;
        });
        
        console.log('Active records:', activeRecords.length);
        setDebugInfo(prev => ({
          ...prev,
          queryAttempts: [...prev.queryAttempts, `Found ${activeRecords.length} active records`]
        }));
        
        if (activeRecords.length > 0) {
          const transportData = activeRecords[0].data();
          const transportId = activeRecords[0].id;
          console.log('Transport data found:', transportData);
          
          // Check if vehicle details are missing and fetch them if needed
          if (transportData.vehicleId && !transportData.vehicleNumber) {
            try {
              const vehicleDocRef = doc(db, 'transportVehicles', transportData.vehicleId);
              const vehicleDocSnap = await getDoc(vehicleDocRef);
              if (vehicleDocSnap.exists()) {
                const vehicle = vehicleDocSnap.data();
                // Update the record with vehicle details
                await updateDoc(doc(db, 'studentTransport', transportId), {
                  vehicleNumber: vehicle.vehicleNumber || null,
                  vehicleType: vehicle.vehicleType || null,
                  driverName: vehicle.driverName || null,
                  driverPhone: vehicle.driverPhone || null,
                  updatedAt: serverTimestamp()
                });
                // The listener will pick up the update on next snapshot
              }
            } catch (error) {
              console.error('Error fetching vehicle details:', error);
            }
          }
          
          // Check if route details are missing and fetch them if needed
          if (transportData.routeId && !transportData.routeName) {
            try {
              const routeDocRef = doc(db, 'transportRoutes', transportData.routeId);
              const routeDocSnap = await getDoc(routeDocRef);
              if (routeDocSnap.exists()) {
                const route = routeDocSnap.data();
                // Update the record with route details
                await updateDoc(doc(db, 'studentTransport', transportId), {
                  routeName: route.routeName || '',
                  startLocation: route.startLocation || '',
                  endLocation: route.endLocation || '',
                  stops: route.stops || [],
                  updatedAt: serverTimestamp()
                });
                  // The listener will pick it up on next snapshot
              }
            } catch (error) {
              console.error('Error fetching route details:', error);
            }
          }
          
          setTransportInfo({ id: transportId, ...transportData });
            setLoading(false);
        } else {
            // No active records, check fallback
            console.log('No active records found, checking fallback...');
            setDebugInfo(prev => ({
              ...prev,
              queryAttempts: [...prev.queryAttempts, 'No active records, checking transportAssignments...']
            }));
            checkFallback();
        }
      } else {
          // No records, check email first
          console.log('Snapshot empty, will try email query...');
          setDebugInfo(prev => ({
            ...prev,
            queryAttempts: [...prev.queryAttempts, 'No studentTransport records by studentId']
          }));
          setLoading(false);
        }
      }, async (error) => {
        console.error('Error loading transport info (composite query):', error);
        console.error('Error code:', error?.code);
        console.error('Error message:', error?.message);
        setDebugInfo(prev => ({
          ...prev,
          lastError: `${error?.code}: ${error?.message}`,
          queryAttempts: [...prev.queryAttempts, `Error: ${error?.message}`]
        }));
        // If index error, try fallback query
        if (error?.code === 'failed-precondition' || 
            error?.message?.includes('index') || 
            error?.message?.includes('requires an index')) {
          console.log('Composite index not available, trying fallback query...');
          if (unsubscribe) unsubscribe();
          unsubscribe = tryFallbackQuery();
        } else {
          setError('Failed to load transport information. Please try refreshing the page.');
          setLoading(false);
        }
      });
    };
    
    // Fallback query: query by studentId only, then filter client-side
    const tryFallbackQuery = () => {
      console.log('Trying fallback query (studentId only)...');
      setDebugInfo(prev => ({
        ...prev,
        queryAttempts: [...prev.queryAttempts, 'Trying fallback query (studentId only)']
      }));
      const fallbackQ = query(
        collection(db, 'studentTransport'),
        where('studentId', '==', currentUser.uid)
      );
      
      return onSnapshot(fallbackQ, async (snapshot) => {
        setError(null);
        // Filter client-side by batchId
        const matchingRecords = snapshot.docs.filter(doc => {
          const data = doc.data();
          return data.batchId === batchId && data.isActive !== false;
        });
        
        if (matchingRecords.length > 0) {
          const transportData = matchingRecords[0].data();
          const transportId = matchingRecords[0].id;
          
          // Check if vehicle details are missing and fetch them if needed
          if (transportData.vehicleId && !transportData.vehicleNumber) {
            try {
              const vehicleDocRef = doc(db, 'transportVehicles', transportData.vehicleId);
              const vehicleDocSnap = await getDoc(vehicleDocRef);
              if (vehicleDocSnap.exists()) {
                const vehicle = vehicleDocSnap.data();
                await updateDoc(doc(db, 'studentTransport', transportId), {
                  vehicleNumber: vehicle.vehicleNumber || null,
                  vehicleType: vehicle.vehicleType || null,
                  driverName: vehicle.driverName || null,
                  driverPhone: vehicle.driverPhone || null,
                  updatedAt: serverTimestamp()
                });
              }
            } catch (error) {
              console.error('Error fetching vehicle details:', error);
            }
          }
          
          // Check if route details are missing and fetch them if needed
          if (transportData.routeId && !transportData.routeName) {
            try {
              const routeDocRef = doc(db, 'transportRoutes', transportData.routeId);
              const routeDocSnap = await getDoc(routeDocRef);
              if (routeDocSnap.exists()) {
                const route = routeDocSnap.data();
                await updateDoc(doc(db, 'studentTransport', transportId), {
                  routeName: route.routeName || '',
                  startLocation: route.startLocation || '',
                  endLocation: route.endLocation || '',
                  stops: route.stops || [],
                  updatedAt: serverTimestamp()
                });
              }
            } catch (error) {
              console.error('Error fetching route details:', error);
            }
          }
          
          setTransportInfo({ id: transportId, ...transportData });
          setLoading(false);
        } else {
          // No matching records, check transportAssignments
          checkFallback();
        }
      }, (error) => {
        console.error('Error loading transport info (fallback query):', error);
        setError('Failed to load transport information. Please try refreshing the page.');
        setLoading(false);
      });
    };
    
    // Check transportAssignments as final fallback
    const checkFallback = async () => {
      try {
        console.log('Checking transportAssignments as fallback...');
        setDebugInfo(prev => ({
          ...prev,
          queryAttempts: [...prev.queryAttempts, 'Checking transportAssignments collection...']
        }));
        
        // Try by studentId first
        let assignmentSnapshot = null;
        try {
          const assignmentQuery = query(
            collection(db, 'transportAssignments'),
            where('batchId', '==', batchId),
            where('studentId', '==', currentUser.uid)
          );
          assignmentSnapshot = await getDocs(assignmentQuery);
          console.log('transportAssignments query (by studentId) returned:', assignmentSnapshot.size, 'documents');
          setDebugInfo(prev => ({
            ...prev,
            assignmentsFound: assignmentSnapshot.size,
            queryAttempts: [...prev.queryAttempts, `Found ${assignmentSnapshot.size} assignment(s) by studentId`]
          }));
        } catch (queryError) {
          console.error('Error querying by studentId:', queryError);
        }
        
        // If no results, try by email
        if (!assignmentSnapshot || assignmentSnapshot.empty) {
          console.log('No assignments found by studentId, trying by email...');
          setDebugInfo(prev => ({
            ...prev,
            queryAttempts: [...prev.queryAttempts, 'Trying query by email...']
          }));
          
          try {
            const emailQuery = query(
              collection(db, 'transportAssignments'),
              where('batchId', '==', batchId),
              where('studentEmail', '==', currentUser.email)
            );
            assignmentSnapshot = await getDocs(emailQuery);
            console.log('transportAssignments query (by email) returned:', assignmentSnapshot.size, 'documents');
            setDebugInfo(prev => ({
              ...prev,
              assignmentsFound: assignmentSnapshot.size,
              queryAttempts: [...prev.queryAttempts, `Found ${assignmentSnapshot.size} assignment(s) by email`]
            }));
          } catch (emailError) {
            console.error('Error querying by email:', emailError);
            setDebugInfo(prev => ({
              ...prev,
              queryAttempts: [...prev.queryAttempts, `Error querying by email: ${emailError.message}`]
            }));
          }
        }
        
        // If still no results, try fallback without batchId
        if (!assignmentSnapshot || assignmentSnapshot.empty) {
          console.log('Trying fallback query without batchId...');
          setDebugInfo(prev => ({
            ...prev,
            queryAttempts: [...prev.queryAttempts, 'Trying fallback (all assignments for user)...']
          }));
          
          try {
            // Try by studentId
            let fallbackQuery = query(
              collection(db, 'transportAssignments'),
              where('studentId', '==', currentUser.uid)
            );
            let fallbackSnapshot = await getDocs(fallbackQuery);
            
            // If no results, try by email
            if (fallbackSnapshot.empty && currentUser.email) {
              fallbackQuery = query(
                collection(db, 'transportAssignments'),
                where('studentEmail', '==', currentUser.email)
              );
              fallbackSnapshot = await getDocs(fallbackQuery);
            }
            
            // Filter by batchId client-side
            assignmentSnapshot = {
              docs: fallbackSnapshot.docs.filter(doc => {
                const data = doc.data();
                return data.batchId === batchId && data.isActive !== false;
              }),
              empty: fallbackSnapshot.docs.filter(doc => {
                const data = doc.data();
                return data.batchId === batchId && data.isActive !== false;
              }).length === 0
            };
            
            console.log('Fallback query returned:', assignmentSnapshot.docs.length, 'matching documents');
            setDebugInfo(prev => ({
              ...prev,
              assignmentsFound: assignmentSnapshot.docs.length,
              queryAttempts: [...prev.queryAttempts, `Fallback found ${assignmentSnapshot.docs.length} assignment(s)`]
            }));
          } catch (fallbackError) {
            console.error('Error in fallback query:', fallbackError);
            setDebugInfo(prev => ({
              ...prev,
              queryAttempts: [...prev.queryAttempts, `Fallback error: ${fallbackError.message}`]
            }));
          }
        }
        
        if (assignmentSnapshot && !assignmentSnapshot.empty && assignmentSnapshot.docs && assignmentSnapshot.docs.length > 0) {
            const assignment = assignmentSnapshot.docs[0].data();
            console.log('Assignment data:', assignment);
            setDebugInfo(prev => ({
              ...prev,
              queryAttempts: [...prev.queryAttempts, 'Assignment found! Creating studentTransport record...']
            }));
            
            if (assignment.isActive !== false) {
              // Fetch route and vehicle details
              let routeData = {};
              let vehicleData = {};
              
              if (assignment.routeId) {
                try {
                  const routeDocRef = doc(db, 'transportRoutes', assignment.routeId);
                  const routeDocSnap = await getDoc(routeDocRef);
                  if (routeDocSnap.exists()) {
                    const route = routeDocSnap.data();
                    routeData = {
                      routeName: route.routeName || '',
                      startLocation: route.startLocation || '',
                      endLocation: route.endLocation || '',
                      stops: route.stops || []
                    };
                  }
                } catch (error) {
                  console.error('Error fetching route:', error);
                }
              }
              
              if (assignment.vehicleId) {
                try {
                  const vehicleDocRef = doc(db, 'transportVehicles', assignment.vehicleId);
                  const vehicleDocSnap = await getDoc(vehicleDocRef);
                  if (vehicleDocSnap.exists()) {
                    const vehicle = vehicleDocSnap.data();
                    vehicleData = {
                      vehicleNumber: vehicle.vehicleNumber || null,
                      vehicleType: vehicle.vehicleType || null,
                      driverName: vehicle.driverName || null,
                      driverPhone: vehicle.driverPhone || null
                    };
                  }
                } catch (error) {
                  console.error('Error fetching vehicle:', error);
                }
              }
              
              // Create studentTransport record from assignment with full details
              const studentTransportData = {
                ...assignment,
                ...routeData,
                ...vehicleData,
                updatedAt: serverTimestamp()
              };
              await addDoc(collection(db, 'studentTransport'), studentTransportData);
              console.log('✅ Created studentTransport record successfully');
              setDebugInfo(prev => ({
                ...prev,
                queryAttempts: [...prev.queryAttempts, '✅ Created studentTransport record - refreshing...']
              }));
              // The listener will pick it up on next snapshot
          } else {
            console.log('Assignment is inactive');
            setDebugInfo(prev => ({
              ...prev,
              queryAttempts: [...prev.queryAttempts, 'Assignment found but marked as inactive']
            }));
            setTransportInfo(null);
            setLoading(false);
            }
        } else {
          console.log('No assignments found');
          setDebugInfo(prev => ({
            ...prev,
            queryAttempts: [...prev.queryAttempts, '❌ No transport assignments found']
          }));
          setTransportInfo(null);
          setLoading(false);
          }
        } catch (error) {
          console.error('Error checking transportAssignments:', error);
        setDebugInfo(prev => ({
          ...prev,
          lastError: error.message,
          queryAttempts: [...prev.queryAttempts, `❌ Error: ${error.message}`]
        }));
        setTransportInfo(null);
      setLoading(false);
      }
    };
    
    // Start with composite query, then try email if that fails
    queriesInitiatedRef.current = true;
    
    const initQueries = async () => {
      unsubscribe = tryCompositeQuery();
      
      // Wait a bit for the snapshot, then try email if nothing found
      setTimeout(async () => {
        // Check if transport was found by checking the state at this moment
        setTransportInfo(current => {
          if (!current) {
            // Try email query
            tryEmailQuery().then(foundByEmail => {
              if (!foundByEmail) {
                checkFallback();
              }
            });
          }
          return current;
        });
      }, 1000);
    };
    
    initQueries();
    
    return () => {
      queriesInitiatedRef.current = false;
      if (unsubscribe) unsubscribe();
    };
  }, [batchId, currentUser?.uid]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Bus className="w-6 h-6 mr-2 text-blue-600" />
          Transport Information
        </h2>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {!transportInfo ? (
        <div className="space-y-6">
          <div className="text-center py-12">
            <Bus className="w-12 h-12 mx-auto mb-4 opacity-50 text-gray-400" />
            <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">No transport information available</p>
            <p className="text-sm mt-2 text-gray-400">Contact admin to register for transport</p>
          </div>
          
          {/* Debug Panel */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <details className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <summary className="cursor-pointer font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-blue-500" />
                Debug Information (Click to expand)
              </summary>
              
              <div className="mt-4 space-y-4">
                {/* User Info */}
                <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2">User Information</h4>
                  <div className="text-xs font-mono space-y-1 text-gray-600 dark:text-gray-400">
                    <div><strong>User ID:</strong> {currentUser?.uid || 'N/A'}</div>
                    <div><strong>Email:</strong> {currentUser?.email || 'N/A'}</div>
                    <div><strong>Batch ID:</strong> {batchId || 'N/A'}</div>
                  </div>
                </div>

                {/* Query Attempts */}
                <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2">Query Attempts</h4>
                  <div className="text-xs space-y-1 text-gray-600 dark:text-gray-400">
                    {debugInfo.queryAttempts.length > 0 ? (
                      debugInfo.queryAttempts.map((attempt, index) => (
                        <div key={index} className="flex items-start">
                          <span className="text-blue-500 mr-2">•</span>
                          <span>{attempt}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-400">No query attempts yet</div>
                    )}
                  </div>
                </div>

                {/* Results */}
                <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2">Query Results</h4>
                  <div className="text-xs space-y-1 text-gray-600 dark:text-gray-400">
                    <div><strong>studentTransport records:</strong> {debugInfo.studentTransportFound}</div>
                    <div><strong>transportAssignments records:</strong> {debugInfo.assignmentsFound}</div>
                  </div>
                </div>

                {/* Error */}
                {debugInfo.lastError && (
                  <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded border border-red-200 dark:border-red-800">
                    <h4 className="font-semibold text-sm text-red-700 dark:text-red-400 mb-2">Last Error</h4>
                    <div className="text-xs font-mono text-red-600 dark:text-red-400">
                      {debugInfo.lastError}
                    </div>
                  </div>
                )}

                {/* Diagnosis */}
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-800">
                  <h4 className="font-semibold text-sm text-blue-700 dark:text-blue-400 mb-2">💡 Diagnosis</h4>
                  <div className="text-xs text-blue-600 dark:text-blue-400 space-y-2">
                    {debugInfo.studentTransportFound === 0 && debugInfo.assignmentsFound === 0 && (
                      <div>
                        <strong>Issue:</strong> No transport records found for this user.<br/>
                        <strong>Solution:</strong> Admin needs to assign transport to this student in Transport Management.
                      </div>
                    )}
                    {debugInfo.assignmentsFound > 0 && debugInfo.studentTransportFound === 0 && (
                      <div>
                        <strong>Issue:</strong> Assignment exists but studentTransport record is missing.<br/>
                        <strong>Solution:</strong> Run the sync tool or re-create the assignment.
                      </div>
                    )}
                    {debugInfo.lastError && debugInfo.lastError.includes('permission') && (
                      <div>
                        <strong>Issue:</strong> Permission denied.<br/>
                        <strong>Solution:</strong> Check Firestore security rules.
                      </div>
                    )}
                    {debugInfo.lastError && debugInfo.lastError.includes('index') && (
                      <div>
                        <strong>Issue:</strong> Missing Firestore index.<br/>
                        <strong>Solution:</strong> The app is using fallback queries, this should work.
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Button */}
                <div className="pt-2">
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                  >
                    Refresh Page
                  </button>
                </div>
              </div>
            </details>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Transport Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Route</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{transportInfo.routeName || transportInfo.routeNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Vehicle Number</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{transportInfo.vehicleNumber || transportInfo.busNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Pickup Point</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{transportInfo.pickupPoint || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Pickup Time</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{transportInfo.pickupTime || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Drop Point</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{transportInfo.dropPoint || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Drop Time</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{transportInfo.dropTime || 'N/A'}</p>
              </div>
              {transportInfo.driverName && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Driver Name</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{transportInfo.driverName}</p>
                </div>
              )}
              {transportInfo.driverContact && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Driver Contact</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{transportInfo.driverContact}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Batch Activities Tab Component
const BatchActivitiesTab = ({ batchId, currentUser, isTeacherOrAdmin }) => {
  const [activityForms, setActivityForms] = useState([]); // Forms created by admin
  const [mySubmissions, setMySubmissions] = useState([]); // Student's filled forms
  const [loading, setLoading] = useState(true);
  const [showFillModal, setShowFillModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const loadActivityForms = useCallback(() => {
    if (!batchId) return;
    
    // Load active forms created by admin for this batch
    // OPTIMIZED: Use getDocs with caching instead of onSnapshot
    const loadActivityForms = async () => {
      try {
        // Check cache first (5 minute TTL)
        const cacheKey = `activityForms_${batchId}`;
        const cached = localStorage.getItem(cacheKey);
        const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
        
        if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
          setActivityForms(JSON.parse(cached));
          setLoading(false);
          return;
        }

        const q = query(
          collection(db, 'activityForms'),
          where('batchId', '==', batchId),
          where('isActive', '==', true),
          limit(20)
        );
        
        const snapshot = await getDocs(q);
        const formsList = [];
        snapshot.forEach((doc) => {
          formsList.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort by createdAt in memory
        formsList.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
          const bTime = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
          return bTime - aTime;
        });
        
        setActivityForms(formsList);
        
        // Cache the results
        localStorage.setItem(cacheKey, JSON.stringify(formsList));
        localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading activity forms:', error);
        setLoading(false);
      }
    };
    
    loadActivityForms();
    
    // Refresh every 5 minutes
    const refreshInterval = setInterval(loadActivityForms, 300000);
    return () => clearInterval(refreshInterval);
  }, [batchId]);


  // OPTIMIZED: Use getDocs with caching instead of onSnapshot
  const loadMySubmissions = useCallback(async () => {
    if (!batchId || !currentUser?.uid) return;
    
    try {
      // Check cache first (5 minute TTL)
      const cacheKey = `activitySubmissions_${batchId}_${currentUser.uid}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        setMySubmissions(JSON.parse(cached));
        setLoading(false);
        return;
      }

      const q = query(
        collection(db, 'activityFormSubmissions'),
        where('batchId', '==', batchId),
        where('studentId', '==', currentUser.uid),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      const submissionsList = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        submissionsList.push({ id: doc.id, ...data });
      });
      
      // Sort client-side by submittedAt descending
      submissionsList.sort((a, b) => {
        const dateA = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
        const dateB = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
        return dateB.getTime() - dateA.getTime();
      });
      
      setMySubmissions(submissionsList);
      
      // Cache the results
      localStorage.setItem(cacheKey, JSON.stringify(submissionsList));
      localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading submissions:', error);
      setLoading(false);
    }
  }, [batchId, currentUser?.uid]);

  // Set up real-time listeners after functions are defined
  useEffect(() => {
    if (!batchId) return;
    
    loadActivityForms();
    loadMySubmissions();
    
    // Refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      loadActivityForms();
      loadMySubmissions();
    }, 300000);
    
    return () => clearInterval(refreshInterval);
  }, [batchId, currentUser?.uid, loadActivityForms, loadMySubmissions]);

  // Helper function to create a key from field label
  const getFieldKey = (field) => {
    if (field.name) return field.name;
    // Create key from label: lowercase, replace spaces with underscores
    return (field.label || 'field').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  const handleFillForm = (form) => {
    setSelectedForm(form);
    // Initialize form data with form fields
    const initialData = {};
    if (form.fields) {
      form.fields.forEach(field => {
        const fieldKey = getFieldKey(field);
        initialData[fieldKey] = '';
      });
    }
    setFormData(initialData);
    setShowFillModal(true);
  };

  const handleSubmitFilledForm = async () => {
    if (!selectedForm) return;

    // Validate required fields
    const requiredFields = selectedForm.fields?.filter(f => f.required) || [];
    for (const field of requiredFields) {
      const fieldKey = getFieldKey(field);
      if (!formData[fieldKey]?.trim()) {
        toast.error(`Please fill in ${field.label || fieldKey}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const submissionData = {
        batchId,
        formId: selectedForm.id,
        formName: selectedForm.title || selectedForm.name || 'Activity Form',
        studentId: currentUser.uid,
        studentName: currentUser.displayName || currentUser.email,
        formData: formData,
        status: 'submitted',
        submittedAt: serverTimestamp()
      };
      console.log('Submitting form with data:', submissionData);
      const docRef = await addDoc(collection(db, 'activityFormSubmissions'), submissionData);
      console.log('Form submitted successfully with ID:', docRef.id);
      toast.success('Form submitted successfully');
      setShowFillModal(false);
      setSelectedForm(null);
      setFormData({});
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Activity className="w-6 h-6 mr-2 text-pink-600" />
          Co-curricular Activities
        </h2>
      </div>

      {/* Available Forms Section */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Available Forms</h3>
        {activityForms.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No activity forms available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activityForms.map((form) => {
              const isSubmitted = mySubmissions.some(s => s.formId === form.id);
              return (
                <div key={form.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {form.name || form.title}
                      </h3>
                      {form.description && (
                        <p className="text-gray-700 dark:text-gray-300 mt-2">{form.description}</p>
                      )}
                    </div>
                    {isSubmitted ? (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        Submitted
                      </span>
                    ) : (
                      <button
                        onClick={() => handleFillForm(form)}
                        className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg transition-colors text-sm"
                      >
                        Fill Form
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Created: {form.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* My Submissions Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">My Submissions</h3>
        {mySubmissions.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>No submissions yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {mySubmissions.map((submission) => (
              <div key={submission.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {submission.formName}
                    </h3>
                    <div className="mt-2 space-y-1">
                      {Object.entries(submission.formData || {}).map(([key, value]) => (
                        <p key={key} className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">{key}:</span> {value}
                        </p>
                      ))}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    submission.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    submission.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}>
                    {submission.status?.charAt(0).toUpperCase() + submission.status?.slice(1) || 'Submitted'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Submitted: {submission.submittedAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fill Form Modal */}
      {showFillModal && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{selectedForm.name || selectedForm.title}</h3>
              <button onClick={() => { setShowFillModal(false); setSelectedForm(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            {selectedForm.description && (
              <p className="text-gray-600 dark:text-gray-400 mb-4">{selectedForm.description}</p>
            )}
            <div className="space-y-4">
              {selectedForm.fields?.map((field, index) => {
                const fieldKey = getFieldKey(field);
                return (
                  <div key={fieldKey || index}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {field.label || fieldKey} {field.required && '*'}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={formData[fieldKey] || ''}
                        onChange={(e) => setFormData({ ...formData, [fieldKey]: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        placeholder={field.placeholder}
                        required={field.required}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        value={formData[fieldKey] || ''}
                        onChange={(e) => setFormData({ ...formData, [fieldKey]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        required={field.required}
                      >
                        <option value="">Select {field.label || fieldKey}</option>
                        {field.options?.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : field.type === 'date' ? (
                      <input
                        type="date"
                        value={formData[fieldKey] || ''}
                        onChange={(e) => setFormData({ ...formData, [fieldKey]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        required={field.required}
                      />
                    ) : (
                      <input
                        type={field.type || 'text'}
                        value={formData[fieldKey] || ''}
                        onChange={(e) => setFormData({ ...formData, [fieldKey]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        placeholder={field.placeholder}
                        required={field.required}
                      />
                    )}
                  </div>
                );
              })}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setShowFillModal(false); setSelectedForm(null); }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitFilledForm}
                  disabled={submitting}
                  className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-md disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Form'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Batch Grievance Tab Component
const BatchGrievanceTab = ({ batchId, currentUser, isTeacherOrAdmin }) => {
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGrievanceModal, setShowGrievanceModal] = useState(false);
  const [grievanceData, setGrievanceData] = useState({
    type: 'grievance', // grievance, suggestion, complaint
    subject: '',
    description: '',
    priority: 'medium' // low, medium, high
  });
  const [submitting, setSubmitting] = useState(false);

  // OPTIMIZED: Use getDocs with caching instead of onSnapshot
  const loadGrievances = useCallback(async () => {
    if (!batchId || !currentUser?.uid) return;
    
    try {
      // Check cache first (5 minute TTL)
      const cacheKey = `grievances_${batchId}_${currentUser.uid}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        setGrievances(JSON.parse(cached));
        setLoading(false);
        return;
      }

      const q = query(
        collection(db, 'grievances'),
        where('batchId', '==', batchId),
        where('studentId', '==', currentUser.uid),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      const grievancesList = [];
      snapshot.forEach((doc) => {
        grievancesList.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort client-side by createdAt descending
      grievancesList.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB.getTime() - dateA.getTime();
      });
      
      setGrievances(grievancesList);
      
      // Cache the results
      localStorage.setItem(cacheKey, JSON.stringify(grievancesList));
      localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading grievances:', error);
      setLoading(false);
    }
  }, [batchId, currentUser?.uid]);

  useEffect(() => {
    loadGrievances();
    
    // Refresh every 5 minutes
    const refreshInterval = setInterval(loadGrievances, 300000);
    return () => clearInterval(refreshInterval);
  }, [batchId, currentUser?.uid, loadGrievances]);

  const handleSubmitGrievance = async () => {
    if (!grievanceData.subject.trim() || !grievanceData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'grievances'), {
        batchId,
        studentId: currentUser.uid,
        studentName: currentUser.displayName || currentUser.email,
        ...grievanceData,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      toast.success('Grievance submitted successfully');
      setShowGrievanceModal(false);
      setGrievanceData({
        type: 'grievance',
        subject: '',
        description: '',
        priority: 'medium'
      });
    } catch (error) {
      toast.error('Failed to submit grievance');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <AlertTriangle className="w-6 h-6 mr-2 text-red-600" />
          Grievance / Suggestions / Complaints
        </h2>
        <button
          onClick={() => setShowGrievanceModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Submit</span>
        </button>
      </div>

      {grievances.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No grievances submitted yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {grievances.map((grievance) => (
            <div key={grievance.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      grievance.type === 'complaint' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                      grievance.type === 'suggestion' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {grievance.type?.charAt(0).toUpperCase() + grievance.type?.slice(1)}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      grievance.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                      grievance.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                      {grievance.priority?.charAt(0).toUpperCase() + grievance.priority?.slice(1)} Priority
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {grievance.subject}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mt-2">{grievance.description}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  grievance.status === 'resolved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  grievance.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                }`}>
                  {grievance.status?.charAt(0).toUpperCase() + grievance.status?.slice(1) || 'Pending'}
                </span>
              </div>
              {grievance.response && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
                  <p className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-1">Response:</p>
                  <p className="text-gray-700 dark:text-gray-300">{grievance.response}</p>
                </div>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Submitted: {grievance.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Grievance Modal */}
      {showGrievanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Submit Grievance</h3>
              <button onClick={() => setShowGrievanceModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type *
                </label>
                <select
                  value={grievanceData.type}
                  onChange={(e) => setGrievanceData({ ...grievanceData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="grievance">Grievance</option>
                  <option value="suggestion">Suggestion</option>
                  <option value="complaint">Complaint</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Priority
                </label>
                <select
                  value={grievanceData.priority}
                  onChange={(e) => setGrievanceData({ ...grievanceData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={grievanceData.subject}
                  onChange={(e) => setGrievanceData({ ...grievanceData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  placeholder="Enter subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  value={grievanceData.description}
                  onChange={(e) => setGrievanceData({ ...grievanceData, description: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  placeholder="Describe your grievance, suggestion, or complaint in detail..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowGrievanceModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitGrievance}
                  disabled={submitting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
                >
                  {submitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Batch Syllabus Tab Component
const BatchSyllabusTab = ({ batchId, isTeacherOrAdmin }) => {
  const [syllabus, setSyllabus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [expandedTopics, setExpandedTopics] = useState({});

  useEffect(() => {
    const loadSyllabus = async () => {
      setLoading(true);
      const result = await getBatchSyllabus(batchId);
      if (result.success && result.data) {
        setSyllabus(result.data);
      }
      setLoading(false);
    };
    loadSyllabus();
  }, [batchId]);

  const toggleSubject = (index) => {
    setExpandedSubjects(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleTopic = (index) => {
    setExpandedTopics(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleToggleSubtopic = async (subjectIndex, topicIndex, subtopicIndex) => {
    if (!isTeacherOrAdmin) return;

    const newSyllabus = { ...syllabus };
    const subtopic = newSyllabus.subjects[subjectIndex].topics[topicIndex].subtopics[subtopicIndex];
    
    subtopic.isDone = !subtopic.isDone;
    subtopic.doneAt = subtopic.isDone ? new Date().toISOString() : null;

    // Auto-update topic status
    const topic = newSyllabus.subjects[subjectIndex].topics[topicIndex];
    const allSubtopicsDone = topic.subtopics.length > 0 && topic.subtopics.every(s => s.isDone);
    
    topic.isDone = allSubtopicsDone;
    if (allSubtopicsDone) {
      // Find the latest doneAt from subtopics
      const latestDoneAt = topic.subtopics.reduce((latest, s) => {
        if (!latest || (s.doneAt && new Date(s.doneAt) > new Date(latest))) {
          return s.doneAt;
        }
        return latest;
      }, null);
      topic.doneAt = latestDoneAt;
    } else {
      topic.doneAt = null;
    }

    setSyllabus(newSyllabus);
    
    // Save to Firestore
    try {
      await saveBatchSyllabus(batchId, newSyllabus.subjects);
      toast.success('Progress updated');
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const calculateSubjectProgress = (subject) => {
    let total = 0;
    let completed = 0;
    subject.topics.forEach(topic => {
      if (topic.subtopics.length === 0) {
        total += 1;
        if (topic.isDone) completed += 1;
      } else {
        total += topic.subtopics.length;
        completed += topic.subtopics.filter(s => s.isDone).length;
      }
    });
    return total === 0 ? 0 : Math.round((completed / total) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!syllabus || !syllabus.subjects || syllabus.subjects.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No syllabus available for this batch yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <BookOpen className="w-6 h-6 mr-2 text-blue-600" />
          Course Syllabus
        </h2>
      </div>

      <div className="space-y-4">
        {syllabus.subjects.map((subject, sIdx) => {
          const progress = calculateSubjectProgress(subject);
          const isExpanded = expandedSubjects[sIdx];

          return (
            <div key={sIdx} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                onClick={() => toggleSubject(sIdx)}
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{subject.name}</h3>
                  </div>
                  <div className="mt-3 flex items-center space-x-4">
                    <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 dark:bg-blue-500 transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{progress}%</span>
                  </div>
                </div>
                <div className="ml-4">
                  {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 dark:border-gray-700 p-4 bg-gray-50/30 dark:bg-gray-800/30">
                  <div className="space-y-3">
                    {subject.topics.map((topic, tIdx) => {
                      const topicKey = `${sIdx}-${tIdx}`;
                      const isTopicExpanded = expandedTopics[topicKey];

                      return (
                        <div key={tIdx} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
                          <div 
                            className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                            onClick={() => toggleTopic(topicKey)}
                          >
                            <div className="flex items-center space-x-3">
                              {topic.isDone ? (
                                <CheckCircle2 className="w-5 h-5 text-green-500" />
                              ) : (
                                <Circle className="w-5 h-5 text-gray-300" />
                              )}
                              <div className="flex flex-col">
                                {topic.isDone && topic.doneAt && (
                                  <span className="text-[10px] text-green-600 dark:text-green-400 font-medium mb-0.5">
                                    Done: {new Date(topic.doneAt).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                )}
                                <span className={`font-semibold ${topic.isDone ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`}>
                                  {topic.name}
                                </span>
                              </div>
                            </div>
                            {topic.subtopics.length > 0 && (
                              <div>
                                {isTopicExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                              </div>
                            )}
                          </div>

                          {isTopicExpanded && topic.subtopics.length > 0 && (
                            <div className="bg-gray-50/50 dark:bg-gray-900/20 px-4 py-2 space-y-2 border-t border-gray-50 dark:border-gray-700">
                              {topic.subtopics.map((subtopic, stIdx) => (
                                <div 
                                  key={stIdx} 
                                  className={`flex items-center justify-between p-2 rounded-lg ${isTeacherOrAdmin ? 'cursor-pointer hover:bg-white dark:hover:bg-gray-800' : ''}`}
                                  onClick={() => handleToggleSubtopic(sIdx, tIdx, stIdx)}
                                >
                                  <div className="flex items-center space-x-3">
                                    {subtopic.isDone ? (
                                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <Circle className="w-4 h-4 text-gray-300" />
                                    )}
                                    <span className={`text-sm ${subtopic.isDone ? 'text-gray-500 line-through' : 'text-gray-700 dark:text-gray-300'}`}>
                                      {subtopic.name}
                                    </span>
                                  </div>
                                  {isTeacherOrAdmin && (
                                    <span className="text-[10px] font-medium text-blue-500 opacity-0 group-hover:opacity-100">Click to toggle</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BatchSchedulePage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useRealtime();
  const [batch, setBatch] = useState(null);
  const [coachingName, setCoachingName] = useState('');
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [doubts, setDoubts] = useState([]);
  const [tests, setTests] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showDoubtModal, setShowDoubtModal] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '' });
  const [announcementFiles, setAnnouncementFiles] = useState([]);
  const [uploadingAnnouncementFiles, setUploadingAnnouncementFiles] = useState(false);
  const [newDoubt, setNewDoubt] = useState({ question: '', subject: '' });
  const [doubtImages, setDoubtImages] = useState([]);
  const [uploadingDoubtImages, setUploadingDoubtImages] = useState(false);
  const [activeTab, setActiveTab] = useState('schedule'); // 'schedule', 'announcements', 'assignments', 'doubts', 'tests', 'timetable', 'results', 'chat', 'attendance', 'transport', 'activities', 'grievance', 'leave', 'profile'

  const loadBatchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get batch details
      const batchResult = await getBatchById(batchId);
      if (!batchResult.success) {
        toast.error('Batch not found');
        navigate('/dashboard');
        return;
      }

      const batchData = batchResult.data;
      setBatch(batchData);

      // Fetch coaching name if not in user profile
      // 1. Check batch document
      // 2. Check user profile (if student has it)
      // 3. Fetch from coaching admin profile
      if (batchData.coachingName) {
        setCoachingName(batchData.coachingName);
      } else if (userProfile?.coachingName) {
        setCoachingName(userProfile.coachingName);
      } else if (batchData.coachingId) {
        try {
          const profileResult = await getUserProfile(batchData.coachingId);
          if (profileResult.success && profileResult.data?.coachingName) {
            setCoachingName(profileResult.data.coachingName);
          }
        } catch (err) {
          console.error('Error fetching coaching name:', err);
        }
      }

      // Check enrollment
      const enrollmentResult = await checkBatchEnrollment(batchId, currentUser.uid);
      if (enrollmentResult.success) {
        setEnrolled(enrollmentResult.enrolled);
        if (!enrollmentResult.enrolled) {
          toast.error('You are not enrolled in this batch');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      toast.error('Failed to load batch');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [batchId, currentUser?.uid, navigate, userProfile?.coachingName]);

  // Load announcements
  // OPTIMIZED: Use getDocs with caching instead of onSnapshot
  const loadAnnouncements = useCallback(async (batchId) => {
    if (!batchId) return;
    
    try {
      // Check cache first (5 minute TTL)
      const cacheKey = `announcements_${batchId}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        setAnnouncements(JSON.parse(cached));
        return;
      }

      // Use the new getBatchAnnouncements function with URL conversion
      const result = await getBatchAnnouncements(batchId);
      
      if (result.success) {
        setAnnouncements(result.data);
        
        // Cache the results
        localStorage.setItem(cacheKey, JSON.stringify(result.data));
        localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
      } else {
        console.error('Error loading announcements:', result.error);
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
  }, []);

  // OPTIMIZED: Use getDocs with caching instead of onSnapshot
  const loadDoubts = useCallback(async (batchId) => {
    if (!batchId) return;
    
    try {
      // Check cache first (5 minute TTL)
      const cacheKey = `doubts_${batchId}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        setDoubts(JSON.parse(cached));
        return;
      }

      const q = query(
        collection(db, 'batchDoubts'),
        where('batchId', '==', batchId),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      const doubtsList = [];
      snapshot.forEach((doc) => {
        doubtsList.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort client-side by createdAt descending
      doubtsList.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB.getTime() - dateA.getTime();
      });
      
      setDoubts(doubtsList);
      
      // Cache the results
      localStorage.setItem(cacheKey, JSON.stringify(doubtsList));
      localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
    } catch (error) {
      console.error('Error loading doubts:', error);
    }
  }, []);

  // OPTIMIZED: Use getDocs with caching instead of onSnapshot
  const loadTests = useCallback(async (batchId, batchCoachingId) => {
    if (!batchId) return;
    
    try {
      // Check cache first (5 minute TTL)
      const cacheKey = `batchTests_${batchId}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        setTests(JSON.parse(cached));
        return;
      }

      // Helper function to filter tests based on live test timing
      const filterLiveTest = (testData) => {
        if (!testData.isLiveTest) return true;
        const now = new Date();
        const startTime = testData.startTime ? new Date(testData.startTime) : null;
        const endTime = testData.endTime ? new Date(testData.endTime) : null;
        if (startTime && endTime) {
          return now >= startTime && now <= endTime;
        } else if (endTime && now > endTime) {
          return false;
        }
        return true;
      };
      
      const q = query(
        collection(db, 'batchTests'),
        where('batchId', '==', batchId),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      const batchTestsList = [];
      snapshot.forEach((doc) => {
        batchTestsList.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort client-side
      batchTestsList.sort((a, b) => {
        const aTime = a.addedAt?.toMillis?.() || new Date(a.addedAt || 0).getTime();
        const bTime = b.addedAt?.toMillis?.() || new Date(b.addedAt || 0).getTime();
        return bTime - aTime;
      });
      
      // Load coaching tests
      let coachingTestsList = [];
      try {
        const allTestsResult = await getTests({});
        if (allTestsResult.success && allTestsResult.data) {
          coachingTestsList = allTestsResult.data.filter(test => {
            const matchesCoaching = batchCoachingId && test.coachingId === batchCoachingId;
            const isPublished = test.status === 'published';
            return matchesCoaching && isPublished && filterLiveTest(test);
          });
        }
      } catch (error) {
        console.error('Error loading coaching tests:', error);
      }
      
      // Combine and process tests
      const testsList = [];
      const processedTestIds = new Set();
      
      for (const batchTest of batchTestsList) {
        try {
          const testResult = await getTestById(batchTest.testId);
          if (testResult.success && testResult.data) {
            const testData = testResult.data;
            if (batchCoachingId && testData.coachingId === null) {
              continue;
            }
            const passesFilter = filterLiveTest(testData);
            testsList.push({
              ...testData,
              batchTestId: batchTest.id,
              isLoading: false,
              isNotAvailable: !passesFilter && testData.isLiveTest
            });
            processedTestIds.add(testData.id);
          }
        } catch (err) {
          console.error('Error loading test:', err);
        }
      }
      
      // Add coaching tests not already in list
      coachingTestsList.forEach(test => {
        if (!processedTestIds.has(test.id)) {
          testsList.push({
            ...test,
            isLoading: false
          });
        }
      });
      
      setTests(testsList);
      
      // Cache the results
      localStorage.setItem(cacheKey, JSON.stringify(testsList));
      localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
    } catch (error) {
      console.error('Error loading tests:', error);
    }
  }, []);

  // Handle announcement file upload
  const handleAnnouncementFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types and sizes
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf'
    ];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not a valid file. Only images and PDFs are allowed.`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB`);
        continue;
      }
    }

    setUploadingAnnouncementFiles(true);
    const uploadedFiles = [];

    try {
      for (const file of files) {
        const fileName = `announcement_${Date.now()}_${file.name}`;
        const storagePath = `batchAnnouncements/${batchId}/${currentUser.uid}/${fileName}`;
        const result = await uploadFile(file, storagePath);
        
        if (result.success) {
          uploadedFiles.push({
            url: result.url,
            name: file.name,
            type: file.type,
            size: file.size
          });
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      setAnnouncementFiles(prev => [...prev, ...uploadedFiles]);
      toast.success('Files uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploadingAnnouncementFiles(false);
    }
  };

  // Remove announcement file
  const handleRemoveAnnouncementFile = (index) => {
    setAnnouncementFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Submit announcement (only for teachers/admins)
  const handleSubmitAnnouncement = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }

    try {
      await addDoc(collection(db, 'batchAnnouncements'), {
        batchId,
        title: newAnnouncement.title,
        content: newAnnouncement.content,
        files: announcementFiles,
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email,
        createdAt: serverTimestamp()
      });
      toast.success('Announcement posted successfully');
      setNewAnnouncement({ title: '', content: '' });
      setAnnouncementFiles([]);
      setShowAnnouncementModal(false);
    } catch (error) {
      toast.error('Failed to post announcement');
    }
  };

  // Handle doubt image upload
  const handleDoubtImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types and sizes
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not a valid image file`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        continue;
      }
    }

    setUploadingDoubtImages(true);
    const uploadedImages = [];

    try {
      for (const file of files) {
        const fileName = `doubt_${Date.now()}_${file.name}`;
        const storagePath = `batchDoubts/${batchId}/${currentUser.uid}/${fileName}`;
        const result = await uploadFile(file, storagePath);
        
        if (result.success) {
          uploadedImages.push(result.url);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      setDoubtImages(prev => [...prev, ...uploadedImages]);
      toast.success('Images uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploadingDoubtImages(false);
    }
  };

  // Remove doubt image
  const handleRemoveDoubtImage = (index) => {
    setDoubtImages(prev => prev.filter((_, i) => i !== index));
  };

  // Submit doubt
  const handleSubmitDoubt = async () => {
    if (!newDoubt.question.trim() && doubtImages.length === 0) {
      toast.error('Please enter your doubt or upload an image');
      return;
    }

    try {
      await addDoc(collection(db, 'batchDoubts'), {
        batchId,
        question: newDoubt.question || '',
        subject: newDoubt.subject || 'General',
        questionImages: doubtImages,
        askedBy: currentUser.uid,
        askedByName: currentUser.displayName || currentUser.email,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      toast.success('Doubt submitted successfully');
      setNewDoubt({ question: '', subject: '' });
      setDoubtImages([]);
      setShowDoubtModal(false);
    } catch (error) {
      toast.error('Failed to submit doubt');
    }
  };

  // Check if user is teacher/admin
  const isTeacherOrAdmin = userProfile?.role === 'admin' || 
                           userProfile?.role === 'superadmin' || 
                           userProfile?.role === 'coaching_admin' ||
                           userProfile?.role === 'teacher' ||
                           currentUser?.role === 'admin' || 
                           currentUser?.role === 'superadmin' || 
                           currentUser?.role === 'coaching_admin' ||
                           currentUser?.role === 'teacher';

  // Answer a doubt (for teachers)
  const [answeringDoubt, setAnsweringDoubt] = useState(null);
  const [doubtAnswer, setDoubtAnswer] = useState('');

  const handleAnswerDoubt = async (doubtId) => {
    if (!doubtAnswer.trim()) {
      toast.error('Please enter an answer');
      return;
    }

    try {
      const doubtRef = doc(db, 'batchDoubts', doubtId);
      await updateDoc(doubtRef, {
        answer: doubtAnswer,
        answeredBy: currentUser.uid,
        answeredByName: currentUser.displayName || currentUser.email,
        status: 'answered',
        answeredAt: serverTimestamp()
      });
      toast.success('Doubt answered successfully');
      setDoubtAnswer('');
      setAnsweringDoubt(null);
    } catch (error) {
      toast.error('Failed to answer doubt');
    }
  };

  useEffect(() => {
    if (!currentUser?.uid) {
      toast.error('Please login to view batches');
      navigate('/login');
      return;
    }

    if (batchId) {
      loadBatchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, currentUser?.uid, loadBatchData]);

  // Set up subscriptions when enrolled
  useEffect(() => {
    if (!enrolled || !batchId) return;

    loadAnnouncements(batchId);
    loadDoubts(batchId);
    loadTests(batchId, batch?.coachingId);
    
    // Refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      loadAnnouncements(batchId);
      loadDoubts(batchId);
      loadTests(batchId, batch?.coachingId);
    }, 300000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [enrolled, batchId, batch?.coachingId, loadAnnouncements, loadDoubts, loadTests]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!enrolled || !batch) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-gray-600 dark:text-gray-400">You are not enrolled in this batch.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {batch.name}
            </h1>
            {batch.description && (
              <p className="text-gray-600 dark:text-gray-400">{batch.description}</p>
            )}
            {batch.instructorName && (
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                Instructor: {batch.instructorName}
              </p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <nav className="flex space-x-4 md:space-x-8 px-4 md:px-6 min-w-max" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('schedule')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'schedule'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Schedule
              </button>
              <button
                onClick={() => setActiveTab('announcements')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'announcements'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Announcements
              </button>
              <button
                onClick={() => setActiveTab('syllabus')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'syllabus'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Syllabus
              </button>
              <button
                onClick={() => setActiveTab('assignments')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'assignments'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Assignments
              </button>
              <button
                onClick={() => setActiveTab('doubts')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'doubts'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Ask Doubts
              </button>
              <button
                onClick={() => setActiveTab('tests')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'tests'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Tests
              </button>
              <button
                onClick={() => setActiveTab('timetable')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'timetable'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Timetable
              </button>
              <button
                onClick={() => setActiveTab('results')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'results'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Results & Admit Cards
              </button>
              <button
                onClick={() => setActiveTab('chat')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'chat'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Group Chat
              </button>
              <button
                onClick={() => setActiveTab('direct-messages')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'direct-messages'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Messages
              </button>
              <button
                onClick={() => setActiveTab('attendance')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'attendance'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Attendance
              </button>
              <button
                onClick={() => setActiveTab('transport')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'transport'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Transport
              </button>
              <button
                onClick={() => setActiveTab('activities')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'activities'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Activities
              </button>
              <button
                onClick={() => setActiveTab('grievance')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'grievance'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Grievance
              </button>
              <button
                onClick={() => setActiveTab('leave')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'leave'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Leave Requests
              </button>
              <button
                onClick={() => setActiveTab('certificates')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'certificates'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Certificates
              </button>
              <button
                onClick={() => setActiveTab('report-cards')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'report-cards'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Report Cards
              </button>
              <button
                onClick={() => setActiveTab('fees')}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'fees'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Fees & Receipts
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'schedule' && (
          <BatchSchedule
            batchId={batchId}
            startDate={batch.startDate}
            endDate={batch.endDate}
          />
        )}

        {activeTab === 'announcements' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <Megaphone className="w-6 h-6 mr-2 text-blue-600" />
                Announcements
              </h2>
              {isTeacherOrAdmin && (
                <button
                  onClick={() => setShowAnnouncementModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Announcement</span>
                </button>
              )}
            </div>

            {announcements.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No announcements yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {announcements.map((announcement) => (
                  <div key={announcement.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {announcement.title}
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {announcement.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                      </span>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {announcement.content}
                    </p>
                    {announcement.files && announcement.files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Attachments:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {announcement.files.map((file, index) => (
                            <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              {file.type && file.type.startsWith('image/') ? (
                                <>
                                  <ImageIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                  <img
                                    src={file.url}
                                    alt={file.name || `Image ${index + 1}`}
                                    className="w-16 h-16 object-cover rounded cursor-pointer"
                                    onClick={() => window.open(file.url, '_blank')}
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                                    {file.name || `Image ${index + 1}`}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <FileText className="w-5 h-5 text-red-600 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <a
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate block"
                                    >
                                      {file.name || `File ${index + 1}`}
                                    </a>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      PDF Document
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      By {announcement.createdByName || 'Admin'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'syllabus' && (
          <BatchSyllabusTab batchId={batchId} isTeacherOrAdmin={isTeacherOrAdmin} />
        )}

        {activeTab === 'assignments' && (
          <BatchAssignmentsTab batchId={batchId} currentUser={currentUser} />
        )}

        {activeTab === 'doubts' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <MessageCircle className="w-6 h-6 mr-2 text-green-600" />
                Ask Doubts
              </h2>
              <button
                onClick={() => setShowDoubtModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Ask Doubt</span>
              </button>
            </div>

            {doubts.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No doubts yet. Be the first to ask!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {doubts.map((doubt) => (
                  <div key={doubt.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                            {doubt.subject}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            doubt.status === 'answered' 
                              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                              : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                          }`}>
                            {doubt.status === 'answered' ? 'Answered' : 'Pending'}
                          </span>
                        </div>
                        <p className="text-gray-900 dark:text-white font-medium mb-2">
                          {doubt.question}
                        </p>
                        {doubt.questionImages && doubt.questionImages.length > 0 && (
                          <div className="mt-2 mb-3 grid grid-cols-3 gap-2">
                            {doubt.questionImages.map((imageUrl, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={imageUrl}
                                  alt={`Question image ${index + 1}`}
                                  className="w-full h-32 object-cover rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer"
                                  onClick={() => window.open(imageUrl, '_blank')}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {doubt.answer ? (
                          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-500">
                            <p className="text-sm font-semibold text-green-800 dark:text-green-200 mb-1">
                              Answer:
                            </p>
                            <p className="text-gray-700 dark:text-gray-300">
                              {doubt.answer}
                            </p>
                            {doubt.answerImages && doubt.answerImages.length > 0 && (
                              <div className="mt-2 grid grid-cols-3 gap-2">
                                {doubt.answerImages.map((imageUrl, index) => (
                                  <div key={index} className="relative group">
                                    <img
                                      src={imageUrl}
                                      alt={`Answer image ${index + 1}`}
                                      className="w-full h-32 object-cover rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer"
                                      onClick={() => window.open(imageUrl, '_blank')}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                            {doubt.answeredBy && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                Answered by {doubt.answeredByName || 'Teacher'}
                              </p>
                            )}
                          </div>
                        ) : isTeacherOrAdmin && doubt.status === 'pending' && (
                          <div className="mt-3">
                            {answeringDoubt === doubt.id ? (
                              <div className="space-y-2">
                                <textarea
                                  value={doubtAnswer}
                                  onChange={(e) => setDoubtAnswer(e.target.value)}
                                  rows={3}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                                  placeholder="Enter your answer..."
                                />
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleAnswerDoubt(doubt.id)}
                                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md"
                                  >
                                    Submit Answer
                                  </button>
                                  <button
                                    onClick={() => {
                                      setAnsweringDoubt(null);
                                      setDoubtAnswer('');
                                    }}
                                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setAnsweringDoubt(doubt.id);
                                  setDoubtAnswer('');
                                }}
                                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md"
                              >
                                Answer This Doubt
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 text-xs text-gray-500 dark:text-gray-400">
                      <span>Asked by {doubt.askedByName || 'Student'}</span>
                      <span>{doubt.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tests' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <FileText className="w-6 h-6 mr-2 text-purple-600" />
                Tests
              </h2>
            </div>

            {tests.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No tests available for this coaching</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tests.map((test) => (
                  <div
                    key={test.id || test.batchTestId}
                    onClick={() => !test.isLoading && test.id && navigate(`/dashboard/test/${test.id}`)}
                    className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-all ${
                      test.isLoading 
                        ? 'opacity-60 cursor-wait' 
                        : 'hover:shadow-lg hover:border-blue-500 dark:hover:border-blue-400 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex-1">
                        {test.title}
                      </h3>
                      {test.isLoading && (
                        <Loader2 className="w-4 h-4 text-blue-500 animate-spin ml-2" />
                      )}
                    </div>
                    {test.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                        {test.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{test.exam || 'General'}</span>
                      <span>{test.duration || 60} min</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'timetable' && (
          <BatchTimetableTab batchId={batchId} coachingId={batch?.coachingId} />
        )}

        {activeTab === 'results' && (
          <BatchResultsTab batchId={batchId} batchCoachingId={batch?.coachingId} currentUser={currentUser} />
        )}

        {activeTab === 'chat' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm h-[calc(100vh-280px)] sm:h-[600px] lg:h-[700px] min-h-[500px]">
            <BatchChat batchId={batchId} />
          </div>
        )}

        {activeTab === 'direct-messages' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm h-[calc(100vh-280px)] sm:h-[600px] lg:h-[700px] min-h-[500px]">
            <BatchDirectMessages batchId={batchId} />
          </div>
        )}

        {activeTab === 'attendance' && (
          <BatchAttendanceTab batchId={batchId} currentUser={currentUser} isTeacherOrAdmin={isTeacherOrAdmin} />
        )}

        {activeTab === 'transport' && (
          <BatchTransportTab batchId={batchId} currentUser={currentUser} isTeacherOrAdmin={isTeacherOrAdmin} />
        )}


        {activeTab === 'activities' && (
          <BatchActivitiesTab batchId={batchId} currentUser={currentUser} isTeacherOrAdmin={isTeacherOrAdmin} />
        )}

        {activeTab === 'grievance' && (
          <BatchGrievanceTab batchId={batchId} currentUser={currentUser} isTeacherOrAdmin={isTeacherOrAdmin} />
        )}

        {activeTab === 'leave' && (
          <LeaveManagement 
            batchId={batchId} 
            currentUser={currentUser} 
            isTeacherOrAdmin={isTeacherOrAdmin}
            showAdminView={false}
            batch={batch}
          />
        )}

        {activeTab === 'certificates' && (
          <BatchCertificatesTab batchId={batchId} currentUser={currentUser} batch={batch} coachingName={coachingName} />
        )}

        {activeTab === 'report-cards' && (
          <BatchReportCardsTab batchId={batchId} currentUser={currentUser} batch={batch} coachingName={coachingName} />
        )}

        {activeTab === 'fees' && (
          <BatchFeesTab batchId={batchId} currentUser={currentUser} batch={batch} />
        )}

        {/* Announcement Modal */}
        {showAnnouncementModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Announcement</h3>
                <button
                  onClick={() => {
                    setShowAnnouncementModal(false);
                    setNewAnnouncement({ title: '', content: '' });
                    setAnnouncementFiles([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter announcement title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Content
                  </label>
                  <textarea
                    value={newAnnouncement.content}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter announcement content"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Upload Files (Images or PDFs) - Optional
                  </label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      onChange={handleAnnouncementFileUpload}
                      disabled={uploadingAnnouncementFiles}
                      className="hidden"
                      id="announcement-file-upload"
                    />
                    <label
                      htmlFor="announcement-file-upload"
                      className={`flex flex-col items-center justify-center cursor-pointer ${uploadingAnnouncementFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {uploadingAnnouncementFiles ? 'Uploading...' : 'Click to upload images or PDFs (Max 10MB each)'}
                      </span>
                    </label>
                  </div>
                  {announcementFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {announcementFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center space-x-2 flex-1">
                            {file.type.startsWith('image/') ? (
                              <ImageIcon className="w-5 h-5 text-blue-600" />
                            ) : (
                              <FileText className="w-5 h-5 text-red-600" />
                            )}
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemoveAnnouncementFile(index)}
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowAnnouncementModal(false);
                      setNewAnnouncement({ title: '', content: '' });
                      setAnnouncementFiles([]);
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitAnnouncement}
                    disabled={uploadingAnnouncementFiles}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Post Announcement
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Doubt Modal */}
        {showDoubtModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Ask a Doubt</h3>
                <button
                  onClick={() => {
                    setShowDoubtModal(false);
                    setNewDoubt({ question: '', subject: '' });
                    setDoubtImages([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={newDoubt.subject}
                    onChange={(e) => setNewDoubt({ ...newDoubt, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Mathematics, Physics, Chemistry"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Doubt
                  </label>
                  <textarea
                    value={newDoubt.question}
                    onChange={(e) => setNewDoubt({ ...newDoubt, question: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Describe your doubt in detail..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Upload Images (Optional)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleDoubtImageUpload}
                      disabled={uploadingDoubtImages}
                      className="hidden"
                      id="doubt-image-upload"
                    />
                    <label
                      htmlFor="doubt-image-upload"
                      className={`flex flex-col items-center justify-center cursor-pointer ${uploadingDoubtImages ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {uploadingDoubtImages ? 'Uploading...' : 'Click to upload images (Max 5MB each)'}
                      </span>
                    </label>
                  </div>
                  {doubtImages.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      {doubtImages.map((imageUrl, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={imageUrl}
                            alt={`Doubt image ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                          />
                          <button
                            onClick={() => handleRemoveDoubtImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDoubtModal(false);
                      setNewDoubt({ question: '', subject: '' });
                      setDoubtImages([]);
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitDoubt}
                    disabled={uploadingDoubtImages}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Submit Doubt
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Batch Timetable Tab Component
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const BatchTimetableTab = ({ batchId, coachingId }) => {
  const [timetables, setTimetables] = useState([]);
  const [masterTimetable, setMasterTimetable] = useState({ slots: [], periods: [] });
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Load PDF timetables (batch-specific or global)
      try {
        const cacheKey = batchId ? `timetables_${batchId}` : 'timetables_global';
        const cached = localStorage.getItem(cacheKey);
        const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
        
        if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
          setTimetables(JSON.parse(cached));
        } else {
          const q = batchId 
            ? query(collection(db, 'timetables'), where('batchId', '==', batchId), limit(20))
            : query(collection(db, 'timetables'), where('isGlobal', '==', true), limit(20));
          
          const snapshot = await getDocs(q);
          const timetablesList = [];
          snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            if (data.pdfUrl) {
              timetablesList.push({
                id: docSnap.id,
                ...data,
                createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0)
              });
            }
          });
          timetablesList.sort((a, b) => b.createdAt - a.createdAt);
          setTimetables(timetablesList);
          localStorage.setItem(cacheKey, JSON.stringify(timetablesList));
          localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
        }
      } catch (error) {
        console.error('Error loading PDF timetables:', error);
      }

      // Load master timetable from Teacher Management
      try {
        const masterResult = await getMasterTimetable(coachingId);
        if (masterResult.success && masterResult.data) {
          const batchSlots = (masterResult.data.slots || []).filter(slot => slot.classId === batchId);
          setMasterTimetable({
            slots: batchSlots,
            periods: masterResult.data.periods || []
          });
        }
      } catch (error) {
        console.error('Error loading master timetable:', error);
      }

      // Load teachers for name lookup
      try {
        const teachersQuery = coachingId 
          ? query(collection(db, 'users'), where('role', '==', 'teacher'), where('coachingId', '==', coachingId))
          : query(collection(db, 'users'), where('role', '==', 'teacher'));
        const teachersSnapshot = await getDocs(teachersQuery);
        const teachersList = [];
        teachersSnapshot.forEach((docSnap) => {
          teachersList.push({ id: docSnap.id, ...docSnap.data() });
        });
        setTeachers(teachersList);
      } catch (error) {
        console.error('Error loading teachers:', error);
      }

      setLoading(false);
    };
    
    loadData();
    const refreshInterval = setInterval(loadData, 300000);
    return () => clearInterval(refreshInterval);
  }, [batchId, coachingId]);

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher?.displayName || teacher?.name || 'Unknown';
  };

  const getSlotForPeriod = (day, period) => {
    return masterTimetable.slots.find(slot => slot.day === day && slot.period === period);
  };

  const hasMasterTimetable = masterTimetable.slots.length > 0 && masterTimetable.periods.length > 0;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Master Timetable Section */}
      {hasMasterTimetable && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Calendar className="w-6 h-6 mr-2 text-blue-600" />
              Class Schedule
            </h2>
          </div>

          {/* Day Selector */}
          <div className="flex flex-wrap gap-2 mb-6">
            {DAYS.map(day => (
              <button
                key={day}
                onClick={() => setSelectedDay(day)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedDay === day
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {day}
              </button>
            ))}
          </div>

          {/* Schedule Grid */}
          <div className="space-y-3">
            {masterTimetable.periods.map((period, idx) => {
              const isBreak = period.toLowerCase().includes('break');
              const slot = getSlotForPeriod(selectedDay, period);
              
              return (
                <div
                  key={idx}
                  className={`rounded-lg p-4 ${
                    isBreak
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                      : slot
                        ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                        : 'bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{period}</p>
                      {isBreak ? (
                        <p className="text-lg font-semibold text-yellow-700 dark:text-yellow-400">Break</p>
                      ) : slot ? (
                        <div>
                          <p className="text-lg font-semibold text-gray-900 dark:text-white">{slot.subject}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Teacher: {getTeacherName(slot.teacherId)}
                            {slot.room && ` • Room: ${slot.room}`}
                          </p>
                        </div>
                      ) : (
                        <p className="text-gray-400 dark:text-gray-500 italic">No class scheduled</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PDF Timetables Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <FileText className="w-6 h-6 mr-2 text-blue-600" />
            {hasMasterTimetable ? 'Additional Timetables' : 'Timetable'}
          </h2>
        </div>

        {timetables.length === 0 && !hasMasterTimetable ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No timetables available for this batch</p>
          </div>
        ) : timetables.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-4">No PDF timetables uploaded</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {timetables.map((timetable) => (
              <div key={timetable.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {timetable.title}
                    </h3>
                    {timetable.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {timetable.description}
                      </p>
                    )}
                  </div>
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                {timetable.pdfUrl && (
                  <a
                    href={timetable.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4" />
                    View/Download PDF
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Batch Results Tab Component
const BatchResultsTab = ({ batchId, batchCoachingId, currentUser }) => {
  const [results, setResults] = useState([]);
  const [admitCards, setAdmitCards] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('results');

  useEffect(() => {
    if (currentUser?.email && batchId) {
      loadDocuments();
    }
  }, [currentUser, activeTab, batchId, batchCoachingId]);

  const loadDocuments = async () => {
    if (!currentUser?.email && !currentUser?.uid) return;
    if (!batchId) return;

    try {
      setLoading(true);
      
      if (activeTab === 'results') {
        // First, get all testIds that belong to this batch via batchTests collection
        const batchTestsQuery = query(
          collection(db, 'batchTests'),
          where('batchId', '==', batchId)
        );
        const batchTestsSnapshot = await getDocs(batchTestsQuery);
        const batchTestIds = new Set();
        batchTestsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.testId) {
            batchTestIds.add(data.testId);
          }
        });
        
        // Filter out admin tests (coachingId = null) for coaching batches
        // Only include tests that belong to the coaching
        const validBatchTestIds = new Set();
        if (batchCoachingId && batchTestIds.size > 0) {
          // For coaching batches, verify each test belongs to the coaching
          try {
            for (const testId of batchTestIds) {
              const testResult = await getTestById(testId);
              if (testResult.success && testResult.data) {
                const test = testResult.data;
                // Only include tests that belong to this coaching (exclude admin tests)
                if (test.coachingId === batchCoachingId) {
                  validBatchTestIds.add(testId);
                }
              }
            }
          } catch (error) {
            // Silently handle errors
          }
        } else {
          // For admin batches (no coachingId), include all tests from batchTests
          batchTestIds.forEach(id => validBatchTestIds.add(id));
        }
        
        // Also get coaching tests that match this batch's coachingId
        // This includes tests created by the coaching (even if not explicitly in batchTests)
        const coachingTestIds = new Set();
        if (batchCoachingId) {
          try {
            const allTestsResult = await getTests({});
            if (allTestsResult.success && allTestsResult.data) {
              allTestsResult.data.forEach(test => {
                // Include tests that match batch coachingId (coaching-created tests)
                // Exclude admin tests (coachingId = null)
                if (test.coachingId === batchCoachingId && test.status === 'published') {
                  coachingTestIds.add(test.id);
                }
              });
            }
          } catch (error) {
            // Silently handle errors
          }
        }
        
        // Combine both sets: tests from batchTests (filtered) + coaching tests matching batch coachingId
        const allValidTestIds = new Set([...validBatchTestIds, ...coachingTestIds]);
        
        // If no tests are linked to this batch, return empty results
        if (allValidTestIds.size === 0) {
          setResults([]);
          setLoading(false);
          return;
        }
        
        // Load evaluated test results directly from testResults collection
        // Include manual evaluation tests - they should appear in batch results section
        const normalizedEmail = currentUser.email?.toLowerCase()?.trim();
        const userId = currentUser.uid;
        
        // Query testResults by userId first (for security rules to work properly)
        // Then filter by status and other conditions
        let resultsQuery;
        try {
          // Try querying by userId first, then filter by status
          resultsQuery = query(
            collection(db, COLLECTIONS.TEST_RESULTS),
            where('userId', '==', userId),
            where('status', '==', 'evaluated')
          );
        } catch (queryError) {
          // If composite index doesn't exist, try without status filter
          try {
            resultsQuery = query(
              collection(db, COLLECTIONS.TEST_RESULTS),
              where('userId', '==', userId)
            );
          } catch (fallbackError) {
            console.error('Error querying test results:', fallbackError);
            setResults([]);
            setLoading(false);
            return;
          }
        }
        
        const querySnapshot = await getDocs(resultsQuery);
        const allResults = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Filter by manual evaluation and by batch
          // Test must be either:
          // 1. In batchTests collection (admin or coaching tests linked to batch), OR
          // 2. A coaching test that matches batch's coachingId
          if (data.manualEvaluation === true &&
              data.testId && allValidTestIds.has(data.testId)) {
            // Also verify email matches (for additional security)
            if ((data.userEmail && data.userEmail.toLowerCase().trim() === normalizedEmail) ||
                (data.userId === userId)) {
              allResults.push({ id: doc.id, ...data });
            }
          }
        });
        
        // Get test information for each result
        const resultsWithTestInfo = await Promise.all(
          allResults.map(async (result) => {
            if (result.testId) {
              try {
                const testsResult = await getTests();
                if (testsResult.success) {
                  const test = testsResult.data.find(t => t.id === result.testId);
                  if (test) {
                    return {
                      ...result,
                      examName: test.title || test.name || 'Test',
                      examDate: test.startTime || result.completedAt || result.createdAt,
                      testData: test
                    };
                  }
                }
              } catch (error) {
                // Error fetching test info
              }
            }
            return {
              ...result,
              examName: result.testName || 'Test',
              examDate: result.completedAt || result.createdAt
            };
          })
        );
        
        // Calculate rank for each result within its test
        // First, get all evaluated results for each test to calculate ranks correctly
        const testIds = [...new Set(resultsWithTestInfo.map(r => r.testId).filter(Boolean))];
        
        // Fetch all evaluated results for each test (for manual evaluation tests)
        // Note: This query might fail for non-admins, so we'll handle it gracefully
        const allTestResults = {};
        for (const testId of testIds) {
          try {
            // Try querying by testId and status (admin/coaching admin only)
            // For students, we'll calculate rank from the results we already have
            let allResultsQuery;
            try {
              allResultsQuery = query(
                collection(db, COLLECTIONS.TEST_RESULTS),
                where('testId', '==', testId),
                where('status', '==', 'evaluated')
              );
            } catch (queryError) {
              // If query fails (permission denied), skip rank calculation for this test
              allTestResults[testId] = [];
              continue;
            }
            const allResultsSnapshot = await getDocs(allResultsQuery);
            const testResults = [];
            allResultsSnapshot.forEach((doc) => {
              const data = doc.data();
              // Only include manual evaluation results
              if (data.manualEvaluation === true) {
                testResults.push({
                  id: doc.id,
                  score: data.score || 0,
                  userEmail: data.userEmail,
                  userId: data.userId
                });
              }
            });
            // Sort by score (descending)
            testResults.sort((a, b) => (b.score || 0) - (a.score || 0));
            allTestResults[testId] = testResults;
          } catch (error) {
            allTestResults[testId] = [];
          }
        }
        
        // Assign rank to each user's result
        resultsWithTestInfo.forEach(result => {
          if (result.testId && allTestResults[result.testId]) {
            const testResults = allTestResults[result.testId];
            const userResult = testResults.find(r => 
              (r.userEmail && result.userEmail && r.userEmail.toLowerCase().trim() === result.userEmail.toLowerCase().trim()) ||
              (r.userId === result.userId)
            );
            
            if (userResult) {
              // Find rank (handle ties - same score gets same rank)
              let rank = 1;
              for (let i = 0; i < testResults.length; i++) {
                if (testResults[i].id === userResult.id) {
                  rank = i + 1;
                  break;
                }
              }
              
              // Adjust for ties (if previous results have same score, use their rank)
              for (let i = rank - 2; i >= 0; i--) {
                if (testResults[i].score === userResult.score) {
                  rank = i + 1;
                } else {
                  break;
                }
              }
              
              result.rank = rank;
              result.totalParticipants = testResults.length;
            }
          }
        });
        
        // Sort by evaluated date (newest first)
        resultsWithTestInfo.sort((a, b) => {
          const dateA = new Date(a.evaluatedAt || a.completedAt || 0).getTime();
          const dateB = new Date(b.evaluatedAt || b.completedAt || 0).getTime();
          return dateB - dateA;
        });
        
        // Also load uploaded result documents from studentResults collection
        const uploadedResultsQuery = await getStudentDocuments(normalizedEmail, 'result');
        if (uploadedResultsQuery.success) {
          const filteredUploadedResults = (uploadedResultsQuery.data || []).filter(doc => 
            doc.batchId === batchId
          );
          // Add uploaded results to the list with a flag to distinguish them
          filteredUploadedResults.forEach(doc => {
            resultsWithTestInfo.push({
              ...doc,
              isUploadedDocument: true,
              documentType: 'result'
            });
          });
        }
        
        setResults(resultsWithTestInfo);
      } else {
        // For admit cards, use the existing method and filter by batchId
        const normalizedEmail = currentUser.email?.toLowerCase()?.trim();
        const result = await getStudentDocuments(normalizedEmail, 'admitCard');
        
        if (result.success) {
          // Filter admit cards by batchId
          const filteredAdmitCards = (result.data || []).filter(card => 
            card.batchId === batchId
          );
          setAdmitCards(filteredAdmitCards);
        } else {
          toast.error(result.error || 'Failed to load admit cards');
        }
      }
    } catch (error) {
      toast.error('Error loading documents: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const documents = activeTab === 'results' ? results : admitCards;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          {activeTab === 'results' ? (
            <Award className="w-6 h-6 mr-2 text-green-600" />
          ) : (
            <FileText className="w-6 h-6 mr-2 text-blue-600" />
          )}
          Results & Admit Cards
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg p-1 mb-6">
        <button
          onClick={() => setActiveTab('results')}
          className={`flex-1 px-4 py-2 rounded flex items-center justify-center gap-2 ${
            activeTab === 'results' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-700 dark:text-gray-300'
          }`}
        >
          <Award className="w-5 h-5" />
          Results
        </button>
        <button
          onClick={() => setActiveTab('admitCards')}
          className={`flex-1 px-4 py-2 rounded flex items-center justify-center gap-2 ${
            activeTab === 'admitCards' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-700 dark:text-gray-300'
          }`}
        >
          <FileText className="w-5 h-5" />
          Admit Cards
        </button>
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {activeTab === 'results' ? (
            <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
          ) : (
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          )}
          <p>No {activeTab === 'results' ? 'results' : 'admit cards'} available yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => {
            const percentage = doc.percentageScore || doc.percentage || 0;
            const marksObtained = doc.score || doc.marksObtained || 0;
            const totalMarks = doc.totalMarks || 0;
            const getGradeColor = (pct) => {
              if (pct >= 90) return 'bg-green-500';
              if (pct >= 75) return 'bg-blue-500';
              if (pct >= 60) return 'bg-orange-500';
              return 'bg-red-500';
            };
            
            return (
              <div 
                key={doc.id} 
                onClick={() => activeTab === 'results' ? setSelectedResult(doc) : null}
                className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow ${activeTab === 'results' ? 'cursor-pointer' : ''}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white flex-1">
                    {doc.examName || doc.testName || 'Document'}
                  </h3>
                  {activeTab === 'results' && percentage > 0 && (
                    <span className={`${getGradeColor(percentage)} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                      {percentage.toFixed(1)}%
                    </span>
                  )}
                </div>
                
                {activeTab === 'results' && totalMarks > 0 && (
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-md text-sm">
                        Marks: {marksObtained}/{totalMarks}
                      </span>
                      {doc.rank && (
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-md text-sm">
                          Rank: #{doc.rank}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {doc.exam && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Exam: {doc.exam}
                  </p>
                )}
                
                {doc.examDate && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Date: {new Date(doc.examDate).toLocaleDateString()}
                  </p>
                )}
                
                {activeTab === 'results' && totalMarks > 0 && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                    Tap to view details
                  </p>
                )}
                
                {doc.documentUrl && (
                  <a
                    href={doc.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm mt-3"
                  >
                    <Download className="w-4 h-4" />
                    View/Download Document
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Result Details Modal */}
      {selectedResult && (() => {
        console.log('Selected Result:', selectedResult);
        console.log('Subject-wise marks:', selectedResult.subjectWiseMarks);
        console.log('Rank:', selectedResult.rank);
        console.log('Exam:', selectedResult.exam);
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedResult(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className={`p-6 rounded-t-lg ${
              (selectedResult.percentageScore || selectedResult.percentage || 0) >= 90 ? 'bg-green-500' :
              (selectedResult.percentageScore || selectedResult.percentage || 0) >= 75 ? 'bg-blue-500' :
              (selectedResult.percentageScore || selectedResult.percentage || 0) >= 60 ? 'bg-orange-500' :
              'bg-red-500'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold text-white">
                  {selectedResult.examName || selectedResult.testName}
                </h2>
                <button
                  onClick={() => setSelectedResult(null)}
                  className="text-white hover:text-gray-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-4xl font-bold text-white text-center mt-4">
                {(selectedResult.percentageScore || selectedResult.percentage || 0).toFixed(1)}%
              </p>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Total Marks</span>
                  <span className="font-bold text-gray-900 dark:text-white">{selectedResult.totalMarks || 0}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Marks Obtained</span>
                  <span className="font-bold text-gray-900 dark:text-white">{selectedResult.score || selectedResult.marksObtained || 0}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">Percentage</span>
                  <span className="font-bold text-gray-900 dark:text-white">{(selectedResult.percentageScore || selectedResult.percentage || 0).toFixed(1)}%</span>
                </div>
                {selectedResult.rank && (
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Rank</span>
                    <span className="font-bold text-gray-900 dark:text-white">#{selectedResult.rank}</span>
                  </div>
                )}
                {selectedResult.exam && (
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Exam</span>
                    <span className="font-bold text-gray-900 dark:text-white">{selectedResult.exam}</span>
                  </div>
                )}
                
                {selectedResult.subjectWiseMarks && Object.keys(selectedResult.subjectWiseMarks).length > 0 && (
                  <>
                    <div className="pt-4">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Subject-wise Performance</h3>
                      <div className="space-y-3">
                        {Object.entries(selectedResult.subjectWiseMarks).map(([subject, marks]) => {
                          const isOldFormat = typeof marks === 'number';
                          const obtained = isOldFormat ? marks : marks.obtained;
                          const total = isOldFormat ? null : marks.total;
                          const percentage = total ? ((obtained / total) * 100).toFixed(1) : null;
                          
                          const getPerformanceLabel = (pct) => {
                            if (!pct) return null;
                            if (pct >= 90) return { label: 'Excellent', color: 'bg-green-500', textColor: 'text-green-700' };
                            if (pct >= 75) return { label: 'Good', color: 'bg-blue-500', textColor: 'text-blue-700' };
                            if (pct >= 60) return { label: 'Average', color: 'bg-yellow-500', textColor: 'text-yellow-700' };
                            if (pct >= 40) return { label: 'Need Improvement', color: 'bg-orange-500', textColor: 'text-orange-700' };
                            return { label: 'Please Revise the Topics', color: 'bg-red-500', textColor: 'text-red-700' };
                          };
                          
                          const performance = percentage ? getPerformanceLabel(parseFloat(percentage)) : null;
                          
                          return (
                            <div key={subject} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-medium text-gray-900 dark:text-white">{subject}</span>
                                <div className="text-right">
                                  <span className="font-bold text-gray-900 dark:text-white">
                                    {obtained}{total ? `/${total}` : ''}
                                  </span>
                                  {percentage && (
                                    <span className="ml-2 text-sm font-semibold text-gray-600 dark:text-gray-300">
                                      ({percentage}%)
                                    </span>
                                  )}
                                </div>
                              </div>
                              {performance && (
                                <div className="flex items-center gap-2 mt-2">
                                  <div className={`flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden`}>
                                    <div 
                                      className={`h-full ${performance.color}`}
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                  <span className={`text-xs font-semibold px-2 py-1 rounded ${performance.color} bg-opacity-20 ${performance.textColor}`}>
                                    {performance.label}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
                
                {!selectedResult.subjectWiseMarks || Object.keys(selectedResult.subjectWiseMarks).length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No subject-wise marks available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

// Batch Profile Tab Component - Moved to Settings.js as StudentProfileTab

// Helper function to add MahStry branding in top right corner
const addMahStryBranding = (doc, pageWidth) => {
  try {
    // Background box for branding
    doc.setFillColor(59, 130, 246);
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    const boxWidth = 45;
    const boxHeight = 12;
    const boxX = pageWidth - boxWidth - 10;
    const boxY = 8;
    doc.rect(boxX, boxY, boxWidth, boxHeight, 'FD');
    
    // MahStry text in white
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('MahStry', pageWidth - 20, boxY + 8, { align: 'right' });
  } catch (error) {
    console.error('Error adding MahStry branding:', error);
    // Fallback: just text
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(59, 130, 246);
    doc.text('MahStry', pageWidth - 10, 15, { align: 'right' });
  }
};

// Helper function to send email (placeholder - can be connected to backend service)
const sendEmail = async (to, subject, body, attachmentData = null) => {
  try {
    // TODO: Implement email sending via backend service
    // For now, we'll use mailto as a fallback
    const mailtoLink = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);
    toast.success('Email client opened. Please send the email manually.');
    return { success: true };
  } catch (error) {
    console.error('Error sending email:', error);
    toast.error('Failed to send email');
    return { success: false, error: error.message };
  }
};

// Batch Certificates Tab Component
const BatchCertificatesTab = ({ batchId, currentUser, batch, coachingName }) => {
  const { userProfile } = useRealtime();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Debug logging
  useEffect(() => {
    console.log('BatchCertificatesTab mounted/updated:', { batchId, batch, currentUser, userProfile });
  }, [batchId, batch, currentUser, userProfile]);

  useEffect(() => {
    const loadCertificates = async () => {
      if (!batchId || !currentUser?.uid) {
        setLoading(false);
        return;
      }
      
      // Get coachingId from batch or userProfile
      const coachingId = batch?.coachingId || userProfile?.coachingId;
      if (!coachingId) {
        console.log('No coachingId found, batch:', batch, 'userProfile:', userProfile);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        console.log('Loading certificates for:', { coachingId, studentId: currentUser.uid, batchId });
        const result = await getCertificates(coachingId, {
          studentId: currentUser.uid,
          batchId: batchId
        });
        
        console.log('Certificates result:', result);
        if (result.success) {
          // Filter by batchId client-side (in case batchId is null in some documents)
          const filtered = (result.data || []).filter(cert => 
            !batchId || cert.batchId === batchId || cert.batchId === null
          );
          setCertificates(filtered);
          console.log('Filtered certificates:', filtered.length, 'out of', result.data?.length || 0);
        } else {
          console.error('Failed to load certificates:', result.error);
          toast.error('Failed to load certificates: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error loading certificates:', error);
        toast.error('Failed to load certificates');
      } finally {
        setLoading(false);
      }
    };

    loadCertificates();
  }, [batchId, batch?.coachingId, currentUser?.uid, userProfile?.coachingId]);

  const generateCertificatePDF = (certificate) => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const templateId = certificate.templateId || 'classic-gold';
    
    // Border
    if (templateId === 'classic-gold') {
      doc.setDrawColor(212, 175, 55); // Gold
      doc.setLineWidth(5);
      doc.rect(10, 10, pageWidth - 20, pageHeight - 20, 'S');
      doc.setLineWidth(1);
      doc.rect(13, 13, pageWidth - 26, pageHeight - 26, 'S');
    } else if (templateId === 'modern-blue') {
      doc.setFillColor(59, 130, 246);
      doc.rect(0, 0, 20, pageHeight, 'F');
      doc.rect(pageWidth - 20, 0, 20, pageHeight, 'F');
    }

    // Logo Placeholder / Institute Name
    const displayCoachingName = coachingName || batch?.coachingName || userProfile?.coachingName || 'MahStry Academy';
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(28);
    doc.setFont(undefined, 'bold');
    doc.text(displayCoachingName, pageWidth / 2, 40, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text('OFFICIAL RECOGNITION OF EXCELLENCE', pageWidth / 2, 50, { align: 'center' });
    
    // Title
    doc.setFontSize(40);
    doc.setTextColor(templateId === 'classic-gold' ? 184 : 59, templateId === 'classic-gold' ? 134 : 130, templateId === 'classic-gold' ? 11 : 246);
    doc.text('CERTIFICATE', pageWidth / 2, 80, { align: 'center' });
    doc.setFontSize(20);
    doc.text('OF ' + certificate.certificateType.toUpperCase(), pageWidth / 2, 92, { align: 'center' });

    // Body
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(16);
    doc.text('This is proudly presented to', pageWidth / 2, 110, { align: 'center' });
    
    doc.setFontSize(32);
    doc.setFont(undefined, 'bold');
    const studentName = (certificate.studentData?.name || certificate.studentData?.displayName || 'Student').toUpperCase();
    doc.text(studentName, pageWidth / 2, 125, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'normal');
    const desc = certificate.description || `for outstanding performance in ${certificate.title}`;
    const descLines = doc.splitTextToSize(desc, pageWidth - 100);
    doc.text(descLines, pageWidth / 2, 140, { align: 'center' });

    if (certificate.venue) {
      doc.setFontSize(12);
      doc.text(`Venue: ${certificate.venue}`, pageWidth / 2, 155, { align: 'center' });
    }

    // Signatures
    const sigY = pageHeight - 40;
    doc.setDrawColor(200, 200, 200);
    
    doc.line(50, sigY, 120, sigY);
    doc.setFontSize(12);
    doc.text(certificate.issuerName || 'Director', 85, sigY + 8, { align: 'center' });
    doc.setFontSize(10);
    doc.text(certificate.issuerTitle || 'Authorized Signatory', 85, sigY + 15, { align: 'center' });

    doc.line(pageWidth - 120, sigY, pageWidth - 50, sigY);
    const date = certificate.achievementDate?.toDate ? certificate.achievementDate.toDate() : new Date(certificate.achievementDate);
    doc.text(date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }), pageWidth - 85, sigY + 8, { align: 'center' });
    doc.text('Date of Achievement', pageWidth - 85, sigY + 15, { align: 'center' });

    doc.save(`Certificate-${studentName}.pdf`);
    toast.success('Certificate downloaded');
  };

  const handleDownload = (certificate) => {
    if (certificate.certificateUrl) {
      window.open(certificate.certificateUrl, '_blank');
      toast.success('Opening certificate...');
    } else {
      generateCertificatePDF(certificate);
    }
  };

  const handleSendEmail = async (certificate) => {
    const studentEmail = currentUser?.email || certificate.studentData?.email;
    if (!studentEmail) {
      toast.error('Email address not found');
      return;
    }

    const subject = `Your Certificate - ${certificate.title || 'Certificate'}`;
    const body = `Dear ${certificate.studentData?.name || 'Student'},\n\nPlease find attached your certificate.\n\nThank you!`;
    
    // Generate PDF and convert to base64 for email attachment
    // For now, we'll just open mailto with the subject and body
    await sendEmail(studentEmail, subject, body);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Award className="w-6 h-6 mr-2 text-yellow-600" />
          My Certificates
        </h2>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-600" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading certificates...</p>
        </div>
      ) : certificates.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No certificates available</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {certificates.map((certificate) => (
            <div
              key={certificate.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {certificate.title || 'Certificate'}
                </h3>
                <span className="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded">
                  {certificate.certificateType}
                </span>
              </div>
              {certificate.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {certificate.description}
                </p>
              )}
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                <span>
                  {certificate.achievementDate?.toDate?.()?.toLocaleDateString() || 'N/A'}
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDownload(certificate)}
                  className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md flex items-center justify-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </button>
                <button
                  onClick={() => handleSendEmail(certificate)}
                  className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md flex items-center justify-center"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Batch Report Cards Tab Component
const BatchReportCardsTab = ({ batchId, currentUser, batch, coachingName }) => {
  const { userProfile } = useRealtime();
  const [reportCards, setReportCards] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Debug logging
  useEffect(() => {
    console.log('BatchReportCardsTab mounted/updated:', { batchId, batch, currentUser, userProfile });
  }, [batchId, batch, currentUser, userProfile]);

  useEffect(() => {
    const loadReportCards = async () => {
      if (!batchId || !currentUser?.uid) {
        setLoading(false);
        return;
      }
      
      // Get coachingId from batch or userProfile
      const coachingId = batch?.coachingId || userProfile?.coachingId;
      if (!coachingId) {
        console.log('No coachingId found, batch:', batch, 'userProfile:', userProfile);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        console.log('Loading report cards for:', { coachingId, studentId: currentUser.uid, batchId });
        const result = await getReportCards(coachingId, {
          studentId: currentUser.uid,
          batchId: batchId
        });
        
        console.log('Report cards result:', result);
        if (result.success) {
          // Filter by batchId client-side (in case batchId is null in some documents)
          const filtered = (result.data || []).filter(card => 
            !batchId || card.batchId === batchId || card.batchId === null
          );
          setReportCards(filtered);
          console.log('Filtered report cards:', filtered.length, 'out of', result.data?.length || 0);
        } else {
          console.error('Failed to load report cards:', result.error);
          toast.error('Failed to load report cards: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error loading report cards:', error);
        toast.error('Failed to load report cards');
      } finally {
        setLoading(false);
      }
    };

    loadReportCards();
  }, [batchId, batch?.coachingId, currentUser?.uid, userProfile?.coachingId]);

  const addSchoolBranding = (doc, pageWidth, yPos = 20) => {
    const nameToDisplay = coachingName || batch?.coachingName || userProfile?.coachingName || 'MahStry Academy';
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59); // Slate-800
    doc.text(nameToDisplay, pageWidth / 2, yPos, { align: 'center' });
    
    // Add subtitle/address if available (simulated)
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 116, 139); // Slate-500
    doc.text('Excellence in Education', pageWidth / 2, yPos + 8, { align: 'center' });
  };

  const generateReportCardPDF = (reportCard) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const templateId = reportCard.templateId || 'modern-clean';
    const displayCoachingName = coachingName || batch?.coachingName || userProfile?.coachingName || 'MahStry Academy';
    
    if (templateId === 'academic-classic') {
      // Classic Border
      doc.setDrawColor(30, 41, 59);
      doc.setLineWidth(1);
      doc.rect(5, 5, pageWidth - 10, doc.internal.pageSize.getHeight() - 10, 'S');
    }

    // Header
    if (templateId === 'modern-clean') {
      doc.setFillColor(59, 130, 246); // Blue
      doc.rect(0, 0, pageWidth, 45, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.text(displayCoachingName, pageWidth / 2, 22, { align: 'center' });
      doc.setFontSize(12);
      doc.text('Official Progress Report', pageWidth / 2, 32, { align: 'center' });
    } else {
      addSchoolBranding(doc, pageWidth, 25);
      doc.setDrawColor(226, 232, 240);
      doc.line(20, 40, pageWidth - 20, 40);
    }
    
    // Student details
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(11);
    let yPos = 60;
    
    const drawDetail = (label, value, x, y) => {
      doc.setFont(undefined, 'bold');
      doc.text(`${label}:`, x, y);
      doc.setFont(undefined, 'normal');
      doc.text(value || 'N/A', x + 35, y);
    };

    drawDetail('Student Name', reportCard.studentData?.name || reportCard.studentData?.displayName || 'Student', 20, yPos);
    drawDetail('Roll No', reportCard.rollNo, pageWidth / 2 + 10, yPos);
    yPos += 10;
    drawDetail('Academic Year', reportCard.academicYear.toString(), 20, yPos);
    drawDetail('Batch', batch?.name || 'N/A', pageWidth / 2 + 10, yPos);
    yPos += 10;
    drawDetail('Term', reportCard.term || 'Annual', 20, yPos);
    drawDetail('Attendance', reportCard.attendance, pageWidth / 2 + 10, yPos);
    yPos += 20;
    
    // Subjects table header
    doc.setFillColor(templateId === 'modern-clean' ? 59 : 30, templateId === 'modern-clean' ? 130 : 41, templateId === 'modern-clean' ? 246 : 59);
    doc.rect(20, yPos, pageWidth - 40, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    
    const col1X = 25;
    const col2X = pageWidth - 110;
    const col3X = pageWidth - 70;
    const col4X = pageWidth - 30;
    
    doc.text('SUBJECT', col1X, yPos + 8);
    doc.text('MARKS', col2X, yPos + 8, { align: 'center' });
    doc.text('MAX', col3X, yPos + 8, { align: 'center' });
    doc.text('GRADE', col4X, yPos + 8, { align: 'center' });
    yPos += 12;
    
    // Subject rows
    doc.setTextColor(30, 41, 59);
    doc.setFont(undefined, 'normal');
    const subjectsToDisplay = reportCard.subjectDetails || [];
    
    subjectsToDisplay.forEach((subject, idx) => {
      if (idx % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(20, yPos, pageWidth - 40, 10, 'F');
      }
      doc.setDrawColor(226, 232, 240);
      doc.line(20, yPos + 10, pageWidth - 20, yPos + 10);
      
      doc.text(subject.name, col1X, yPos + 7);
      doc.text(subject.marks.toString(), col2X, yPos + 7, { align: 'center' });
      doc.text(subject.maxMarks.toString(), col3X, yPos + 7, { align: 'center' });
      doc.text(reportCard.grades?.[subject.name] || '-', col4X, yPos + 7, { align: 'center' });
      yPos += 10;
    });
    
    yPos += 15;
    
    // Summary Box
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    doc.rect(pageWidth - 90, yPos, 70, 45);
    
    doc.setFont(undefined, 'bold');
    doc.text('RESULT SUMMARY', pageWidth - 85, yPos + 8);
    doc.setFont(undefined, 'normal');
    doc.line(pageWidth - 90, yPos + 12, pageWidth - 20, yPos + 12);
    
    const drawSummaryLine = (label, value, y) => {
      doc.text(label, pageWidth - 85, y);
      doc.text(value, pageWidth - 25, y, { align: 'right' });
    };

    drawSummaryLine('Total Marks:', reportCard.obtainedMarks.toString(), yPos + 20);
    drawSummaryLine('Out of:', reportCard.totalMarks.toString(), yPos + 28);
    drawSummaryLine('Percentage:', `${reportCard.percentage}%`, yPos + 36);
    doc.setFont(undefined, 'bold');
    drawSummaryLine('Final Grade:', reportCard.grade, yPos + 44);
    
    // Remarks
    if (reportCard.remarks) {
      doc.setFont(undefined, 'bold');
      doc.text('REMARKS:', 20, yPos + 8);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      doc.text(reportCard.remarks, 20, yPos + 16, { maxWidth: 90 });
    }
    
    // Signatures
    const sigY = doc.internal.pageSize.getHeight() - 40;
    doc.setFontSize(10);
    doc.line(20, sigY, 70, sigY);
    doc.text('Class Teacher', 45, sigY + 7, { align: 'center' });
    if (reportCard.teacherName) doc.text(reportCard.teacherName, 45, sigY - 5, { align: 'center' });

    doc.line(pageWidth - 70, sigY, pageWidth - 20, sigY);
    doc.text('Principal', pageWidth - 45, sigY + 7, { align: 'center' });
    if (reportCard.principalName) doc.text(reportCard.principalName, pageWidth - 45, sigY - 5, { align: 'center' });
    
    doc.save(`ReportCard-${reportCard.studentData?.name || 'Student'}.pdf`);
    toast.success('Report card downloaded');
  };

  const handleDownload = (reportCard) => {
    if (reportCard.reportCardUrl) {
      window.open(reportCard.reportCardUrl, '_blank');
      toast.success('Opening report card...');
    } else {
      generateReportCardPDF(reportCard);
    }
  };

  const handleSendEmail = async (reportCard) => {
    const studentEmail = currentUser?.email || reportCard.studentData?.email;
    if (!studentEmail) {
      toast.error('Email address not found');
      return;
    }

    const subject = `Your Report Card - ${reportCard.academicYear} ${reportCard.term || ''}`;
    const body = `Dear ${reportCard.studentData?.name || 'Student'},\n\nPlease find attached your report card for ${reportCard.academicYear} ${reportCard.term || ''}.\n\nThank you!`;
    
    await sendEmail(studentEmail, subject, body);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <FileText className="w-6 h-6 mr-2 text-blue-600" />
          My Report Cards
        </h2>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-600" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading report cards...</p>
        </div>
      ) : reportCards.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No report cards available</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportCards.map((reportCard) => (
            <div
              key={reportCard.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {reportCard.academicYear}
                </h3>
                {reportCard.term && (
                  <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                    {reportCard.term}
                  </span>
                )}
              </div>
              <div className="space-y-2 mb-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Percentage:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{reportCard.percentage}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Grade:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{reportCard.grade}</span>
                </div>
                {reportCard.rank && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Rank:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{reportCard.rank}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                <span>
                  {reportCard.generatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDownload(reportCard)}
                  className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md flex items-center justify-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </button>
                <button
                  onClick={() => handleSendEmail(reportCard)}
                  className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md flex items-center justify-center"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Batch Fees Tab Component
const BatchFeesTab = ({ batchId, currentUser, batch }) => {
  const { userProfile } = useRealtime();
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingFeeId, setPayingFeeId] = useState(null);
  const [schoolPayoutStatus, setSchoolPayoutStatus] = useState(null);
  const [coachingOwnerId, setCoachingOwnerId] = useState(null);
  
  // Helper function to calculate late fee
  const calculateLateFee = (fee, paymentDate = new Date()) => {
    if (!fee.lateFeeEnabled || !fee.dueDate) return 0;
    
    const dueDate = fee.dueDate?.toDate ? fee.dueDate.toDate() : new Date(fee.dueDate);
    const now = paymentDate;
    
    if (now <= dueDate) return 0; // Not late
    
    const daysLate = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
    if (daysLate <= 0) return 0;
    
    const feeAmount = parseFloat(fee.amount) || 0;
    
    switch (fee.lateFeeType) {
      case 'fixed':
        return parseFloat(fee.lateFeeAmount) || 0;
      
      case 'perDay':
        return daysLate * (parseFloat(fee.lateFeeAmount) || 0);
      
      case 'percentage':
        return (feeAmount * (parseFloat(fee.lateFeePercentage) || 0)) / 100;
      
      case 'slab':
        if (!fee.lateFeeSlabs || fee.lateFeeSlabs.length === 0) return 0;
        // Find the applicable slab
        const sortedSlabs = [...fee.lateFeeSlabs].sort((a, b) => a.fromDay - b.fromDay);
        for (const slab of sortedSlabs) {
          if (daysLate >= slab.fromDay && (slab.toDay === null || slab.toDay === '' || daysLate <= slab.toDay)) {
            return parseFloat(slab.amount) || 0;
          }
        }
        // If days exceed all slabs, use the last slab's amount
        const lastSlab = sortedSlabs[sortedSlabs.length - 1];
        if (daysLate > (lastSlab.toDay || 0)) {
          return parseFloat(lastSlab.amount) || 0;
        }
        return 0;
      
      default:
        return 0;
    }
  };

  // Helper to get days late
  const getDaysLate = (fee) => {
    if (!fee.dueDate) return 0;
    const dueDate = fee.dueDate?.toDate ? fee.dueDate.toDate() : new Date(fee.dueDate);
    const now = new Date();
    if (now <= dueDate) return 0;
    return Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
  };

  // Debug logging
  useEffect(() => {
    console.log('BatchFeesTab mounted/updated:', { batchId, batch, currentUser, userProfile });
  }, [batchId, batch, currentUser, userProfile]);

  // Find coaching owner and check if they can receive Route payments
  useEffect(() => {
    const findCoachingOwnerAndCheckPayout = async () => {
      const coachingId = batch?.coachingId;
      if (!coachingId) return;
      
      try {
        const { collection, query, where, getDocs, limit } = await import('firebase/firestore');
        const { db } = await import('../../firebase/config');
        
        // Find the coaching admin (owner) who has the linked Razorpay account
        const ownerQuery = query(
          collection(db, 'users'),
          where('coachingId', '==', coachingId),
          where('role', '==', 'coaching_admin'),
          limit(1)
        );
        const ownerSnapshot = await getDocs(ownerQuery);
        
        if (!ownerSnapshot.empty) {
          const ownerId = ownerSnapshot.docs[0].id;
          setCoachingOwnerId(ownerId);
          
          // Check if owner can receive Route payments
          const { checkRecipientPayoutStatus } = await import('../../utils/routePaymentHelper');
          const status = await checkRecipientPayoutStatus(ownerId);
          setSchoolPayoutStatus(status);
          console.log('Coaching owner payout status:', { ownerId, status });
        }
      } catch (error) {
        console.log('Could not find coaching owner or check payout status:', error);
      }
    };
    findCoachingOwnerAndCheckPayout();
  }, [batch?.coachingId]);

  useEffect(() => {
    const loadFees = async () => {
      if (!batchId || !currentUser?.uid) {
        setLoading(false);
        return;
      }
      
      // Get coachingId from batch or userProfile
      const coachingId = batch?.coachingId || userProfile?.coachingId;
      if (!coachingId) {
        console.log('No coachingId found, batch:', batch, 'userProfile:', userProfile);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        console.log('Loading fees for:', { coachingId, studentId: currentUser.uid, batchId });
        const result = await getStudentFees(coachingId, {
          studentId: currentUser.uid,
          batchId: batchId
        });
        
        console.log('Fees result:', result);
        if (result.success) {
          // Filter by batchId client-side (in case batchId is null in some documents)
          const filtered = (result.data || []).filter(fee => 
            !batchId || fee.batchId === batchId || fee.batchId === null
          );
          setFees(filtered);
          console.log('Filtered fees:', filtered.length, 'out of', result.data?.length || 0);
        } else {
          console.error('Failed to load fees:', result.error);
          toast.error('Failed to load fees: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error loading fees:', error);
        toast.error('Failed to load fees');
      } finally {
        setLoading(false);
      }
    };

    loadFees();
  }, [batchId, batch?.coachingId, currentUser?.uid, userProfile?.coachingId]);

  const generateReceiptPDF = async (fee) => {
    // Try to get receipt from payment
    let receiptData = null;
    if (fee.paymentId) {
      const receiptResult = await getFeeReceiptByPaymentId(fee.paymentId);
      if (receiptResult.success && receiptResult.data) {
        receiptData = receiptResult.data;
      }
    }

    // If no receipt found, generate one from fee data
    if (!receiptData) {
      receiptData = {
        receiptNumber: `REC-${fee.id?.substring(0, 8).toUpperCase() || 'N/A'}`,
        coachingName: userProfile?.coachingName || 'School',
        studentName: fee.studentData?.name || fee.studentData?.displayName || 'Student',
        feeType: fee.feeType || 'Tuition Fee',
        academicYear: fee.academicYear || 'N/A',
        term: fee.term || 'N/A',
        amount: fee.paidAmount || fee.totalAmount || 0,
        paymentMethod: 'Cash',
        issuedAt: fee.updatedAt || fee.createdAt || new Date()
      };
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Add MahStry branding in top right first
    addMahStryBranding(doc, pageWidth);
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text(receiptData.coachingName || 'School', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont(undefined, 'normal');
    doc.text('Fee Payment Receipt', pageWidth / 2, 30, { align: 'center' });
    
    // Ensure text color is set for all subsequent text
    doc.setTextColor(0, 0, 0);
    
    // Receipt details
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    let yPos = 50;
    doc.text(`Receipt Number: ${receiptData.receiptNumber || 'N/A'}`, 20, yPos);
    yPos += 10;
    let receiptDate = new Date();
    if (receiptData.issuedAt) {
      if (receiptData.issuedAt.toDate) {
        receiptDate = receiptData.issuedAt.toDate();
      } else if (receiptData.issuedAt instanceof Date) {
        receiptDate = receiptData.issuedAt;
      } else {
        receiptDate = new Date(receiptData.issuedAt);
      }
    }
    doc.text(`Date: ${receiptDate.toLocaleDateString()}`, 20, yPos);
    yPos += 10;
    doc.text(`Student: ${receiptData.studentName || 'N/A'}`, 20, yPos);
    yPos += 10;
    doc.text(`Fee Type: ${receiptData.feeType || 'N/A'}`, 20, yPos);
    yPos += 10;
    doc.text(`Academic Year: ${receiptData.academicYear || 'N/A'}`, 20, yPos);
    yPos += 10;
    doc.text(`Term: ${receiptData.term || 'N/A'}`, 20, yPos);
    yPos += 15;
    
    // Payment details
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('Payment Details', 20, yPos);
    yPos += 10;
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const amount = typeof receiptData.amount === 'number' 
      ? receiptData.amount 
      : parseFloat(receiptData.amount) || 0;
    const formattedAmount = amount.toLocaleString('en-IN', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 2 
    });
    doc.text(`Amount: Rs. ${formattedAmount}`, 20, yPos);
    yPos += 10;
    doc.text(`Payment Method: ${receiptData.paymentMethod || 'N/A'}`, 20, yPos);
    if (receiptData.transactionId) {
      yPos += 10;
      doc.text(`Transaction ID: ${receiptData.transactionId}`, 20, yPos);
    }
    
    // Footer
    yPos = doc.internal.pageSize.getHeight() - 20;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('This is a computer-generated receipt.', pageWidth / 2, yPos, { align: 'center' });
    
    doc.save(`Receipt-${receiptData.receiptNumber || 'Receipt'}.pdf`);
    toast.success('Receipt downloaded');
  };

  const handleDownloadReceipt = async (fee) => {
    await generateReceiptPDF(fee);
  };

  const handleSendEmail = async (fee) => {
    const studentEmail = currentUser?.email || fee.studentData?.email;
    if (!studentEmail) {
      toast.error('Email address not found');
      return;
    }

    const subject = `Fee Reminder - ${fee.feeType || 'Fee Payment'}`;
    const outstandingAmount = fee.outstandingAmount || 0;
    const body = outstandingAmount > 0
      ? `Dear ${fee.studentData?.name || 'Student'},\n\nThis is a reminder that you have an outstanding fee of Rs. ${outstandingAmount.toLocaleString('en-IN')} for ${fee.feeType || 'Fee Payment'}.\n\nPlease make the payment at your earliest convenience.\n\nThank you!`
      : `Dear ${fee.studentData?.name || 'Student'},\n\nYour fee payment for ${fee.feeType || 'Fee Payment'} has been received.\n\nThank you!`;
    
    await sendEmail(studentEmail, subject, body);
  };

  // Handle Pay Now - uses Route with 0% commission
  const handlePayFee = async (fee) => {
    if (!fee.outstandingAmount || fee.outstandingAmount <= 0) {
      toast.error('No outstanding amount to pay');
      return;
    }

    setPayingFeeId(fee.id);

    try {
      const { initializeRouteCheckout, checkRecipientPayoutStatus } = await import('../../utils/routePaymentHelper');
      const { recordFeePayment } = await import('../../firebase/collections');
      
      // Calculate late fee
      const lateFee = calculateLateFee(fee);
      const baseAmount = fee.outstandingAmount;
      const totalAmount = baseAmount + lateFee;
      
      const recipientUserId = coachingOwnerId; // Use coaching owner instead of batch.userId
      const coachingId = batch?.coachingId || userProfile?.coachingId;
      
      if (!recipientUserId) {
        // Fallback to regular Razorpay payment without Route
        console.log('No coaching owner found, using regular payment');
        await openRegularPayment(fee, totalAmount, coachingId, lateFee);
        return;
      }

      // Check if school can receive Route payments
      let canUseRoute = schoolPayoutStatus?.canReceive;
      if (!canUseRoute) {
        // Re-check in case status wasn't loaded
        const status = await checkRecipientPayoutStatus(recipientUserId);
        canUseRoute = status?.canReceive;
      }

      if (canUseRoute) {
        // Use Route payment - 0% commission, full amount to school
        await initializeRouteCheckout({
          amount: totalAmount,
          recipientUserId: recipientUserId,
          paymentType: 'fee_payment', // 0% commission
          payerDetails: {
            name: currentUser?.displayName || userProfile?.name || 'Student',
            email: currentUser?.email || '',
            phone: userProfile?.phone || ''
          },
          notes: {
            feeId: fee.id,
            batchId: batchId,
            feeType: fee.feeType,
            studentId: currentUser?.uid,
            coachingId: coachingId,
            lateFeeAmount: lateFee,
            baseFeeAmount: baseAmount
          },
          razorpayKeyId: process.env.REACT_APP_RAZORPAY_KEY_ID,
          onSuccess: async (response) => {
            try {
              // Record the payment with late fee details
              const paymentResult = await recordFeePayment({
                feeId: fee.id,
                studentId: currentUser?.uid,
                coachingId: coachingId,
                batchId: batchId,
                amount: totalAmount,
                paymentMethod: 'razorpay',
                paymentId: response.razorpay_payment_id,
                transactionId: response.razorpay_payment_id,
                isRoutePayment: true,
                platformFee: 0,
                schoolAmount: totalAmount,
                lateFeeApplied: lateFee,
                lateFeeIncluded: lateFee > 0
              });
              
              if (!paymentResult.success) {
                console.error('Failed to record payment:', paymentResult.error);
                toast.error('Payment received but failed to update fee status. Please contact support.');
                setPayingFeeId(null);
                return;
              }
              
              const successMsg = lateFee > 0 
                ? `Payment successful! ₹${totalAmount.toLocaleString('en-IN')} paid (includes ₹${lateFee.toLocaleString('en-IN')} late fee).`
                : `Payment successful! ₹${totalAmount.toLocaleString('en-IN')} paid.`;
              toast.success(successMsg);
              
              // Reload fees
              const result = await getStudentFees(coachingId, {
                studentId: currentUser.uid,
                batchId: batchId
              });
              if (result.success) {
                const filtered = (result.data || []).filter(f => 
                  !batchId || f.batchId === batchId || f.batchId === null
                );
                setFees(filtered);
              }
            } catch (error) {
              console.error('Error recording payment:', error);
              toast.error('Payment successful but failed to update records. Please contact support.');
            }
            setPayingFeeId(null);
          },
          onFailure: (error) => {
            toast.error(error.error || 'Payment failed. Please try again.');
            setPayingFeeId(null);
          },
          onDismiss: () => {
            setPayingFeeId(null);
          }
        });
      } else {
        // Fallback to regular Razorpay payment
        await openRegularPayment(fee, totalAmount, coachingId, lateFee);
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error(error.message || 'Failed to initiate payment');
      setPayingFeeId(null);
    }
  };

  // Regular Razorpay payment (fallback when Route is not available)
  const openRegularPayment = async (fee, amount, coachingId, lateFee = 0) => {
    const { recordFeePayment } = await import('../../firebase/collections');
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../../firebase/config');
    
    // Try to get the school owner's business name
    let businessName = batch?.schoolName || userProfile?.coachingName || 'School';
    if (coachingOwnerId) {
      try {
        const ownerDoc = await getDoc(doc(db, 'users', coachingOwnerId));
        if (ownerDoc.exists()) {
          const ownerData = ownerDoc.data();
          businessName = ownerData.bankDetails?.businessName || 
                         ownerData.coachingName || 
                         ownerData.displayName || 
                         batch?.schoolName || 
                         'School';
        }
      } catch (err) {
        console.log('Could not fetch owner business name:', err);
      }
    }
    
    const razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
    
    // Build description with late fee info
    let description = `Fee Payment to ${businessName} - ${fee.feeType || 'Tuition Fee'}`;
    if (lateFee > 0) {
      description += ` (includes ₹${lateFee.toLocaleString('en-IN')} late fee)`;
    }
    
    const options = {
      key: razorpayKeyId,
      amount: Math.round(amount * 100), // Amount in paise
      currency: 'INR',
      name: businessName,
      description: description,
      prefill: {
        name: currentUser?.displayName || userProfile?.name || 'Student',
        email: currentUser?.email || '',
        contact: userProfile?.phone || ''
      },
      notes: {
        feeId: fee.id,
        batchId: batchId,
        studentId: currentUser?.uid,
        lateFeeAmount: lateFee,
        baseFeeAmount: fee.outstandingAmount
      },
      theme: {
        color: '#3B82F6'
      },
      handler: async function(response) {
        try {
          const paymentResult = await recordFeePayment({
            feeId: fee.id,
            studentId: currentUser?.uid,
            coachingId: coachingId,
            batchId: batchId,
            amount: amount,
            paymentMethod: 'razorpay',
            paymentId: response.razorpay_payment_id,
            transactionId: response.razorpay_payment_id,
            isRoutePayment: false,
            lateFeeApplied: lateFee,
            lateFeeIncluded: lateFee > 0
          });
          
          if (!paymentResult.success) {
            console.error('Failed to record payment:', paymentResult.error);
            toast.error('Payment received but failed to update fee status. Please contact support.');
            setPayingFeeId(null);
            return;
          }
          
          const successMsg = lateFee > 0 
            ? `Payment successful! ₹${amount.toLocaleString('en-IN')} paid (includes ₹${lateFee.toLocaleString('en-IN')} late fee).`
            : `Payment successful! ₹${amount.toLocaleString('en-IN')} paid.`;
          toast.success(successMsg);
          
          // Reload fees
          const result = await getStudentFees(coachingId, {
            studentId: currentUser.uid,
            batchId: batchId
          });
          if (result.success) {
            const filtered = (result.data || []).filter(f => 
              !batchId || f.batchId === batchId || f.batchId === null
            );
            setFees(filtered);
          }
        } catch (error) {
          console.error('Error recording payment:', error);
          toast.error('Payment successful but failed to update records.');
        }
        setPayingFeeId(null);
      },
      modal: {
        ondismiss: function() {
          setPayingFeeId(null);
        }
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.on('payment.failed', function(response) {
      toast.error(response.error?.description || 'Payment failed');
      setPayingFeeId(null);
    });
    razorpay.open();
  };

  const outstandingFees = fees.filter(f => (f.outstandingAmount || 0) > 0);
  const paidFees = fees.filter(f => (f.outstandingAmount || 0) <= 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Receipt className="w-6 h-6 mr-2 text-green-600" />
          My Fees & Receipts
        </h2>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-600" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading fees...</p>
        </div>
      ) : fees.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No fees records available</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="text-sm text-red-600 dark:text-red-400 mb-1">Outstanding (with late fees)</div>
              <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                Rs. {outstandingFees.reduce((sum, f) => sum + (f.outstandingAmount || 0) + calculateLateFee(f), 0).toLocaleString('en-IN')}
              </div>
              {outstandingFees.some(f => calculateLateFee(f) > 0) && (
                <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                  Includes Rs. {outstandingFees.reduce((sum, f) => sum + calculateLateFee(f), 0).toLocaleString('en-IN')} late fees
                </div>
              )}
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="text-sm text-green-600 dark:text-green-400 mb-1">Paid</div>
              <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                Rs. {paidFees.reduce((sum, f) => sum + (f.paidAmount || 0), 0).toLocaleString('en-IN')}
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="text-sm text-blue-600 dark:text-blue-400 mb-1">Total Fees</div>
              <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                Rs. {fees.reduce((sum, f) => sum + (f.totalAmount || f.amount || 0), 0).toLocaleString('en-IN')}
              </div>
            </div>
          </div>

          {/* Outstanding Fees */}
          {outstandingFees.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Outstanding Fees</h3>
              <div className="space-y-3">
                {outstandingFees.map((fee) => {
                  const lateFee = calculateLateFee(fee);
                  const daysLate = getDaysLate(fee);
                  const totalDue = (fee.outstandingAmount || 0) + lateFee;
                  
                  return (
                    <div
                      key={fee.id}
                      className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/20"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white">{fee.feeType || 'Fee'}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {fee.academicYear} {fee.term ? `- ${fee.term}` : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-red-600 dark:text-red-400">
                            Rs. {(fee.outstandingAmount || 0).toLocaleString('en-IN')}
                          </div>
                          {fee.dueDate && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Due: {fee.dueDate.toDate?.()?.toLocaleDateString() || 'N/A'}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Late Fee Breakdown */}
                      {fee.lateFeeEnabled && lateFee > 0 && (
                        <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                          <div className="text-sm text-orange-800 dark:text-orange-200">
                            <div className="flex justify-between mb-1">
                              <span>Outstanding Fee:</span>
                              <span>Rs. {(fee.outstandingAmount || 0).toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex justify-between mb-1 text-orange-600 dark:text-orange-400">
                              <span>Late Fee ({daysLate} days late):</span>
                              <span>+ Rs. {lateFee.toLocaleString('en-IN')}</span>
                            </div>
                            <div className="text-xs text-orange-500 dark:text-orange-400 mb-2">
                              {fee.lateFeeType === 'fixed' && 'Fixed late fee charge'}
                              {fee.lateFeeType === 'perDay' && `Rs. ${fee.lateFeeAmount}/day × ${daysLate} days`}
                              {fee.lateFeeType === 'percentage' && `${fee.lateFeePercentage}% of fee amount`}
                              {fee.lateFeeType === 'slab' && 'Slab-based late fee'}
                            </div>
                            <div className="flex justify-between font-bold border-t border-orange-300 dark:border-orange-700 pt-2">
                              <span>Total to Pay:</span>
                              <span>Rs. {totalDue.toLocaleString('en-IN')}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex space-x-2 mt-3">
                        {/* Pay Now Button - Always visible */}
                        <button
                          onClick={() => handlePayFee(fee)}
                          disabled={payingFeeId === fee.id}
                          className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm rounded-md flex items-center justify-center"
                        >
                          {payingFeeId === fee.id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pay Rs. {totalDue.toLocaleString('en-IN')}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Paid Fees */}
          {paidFees.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Paid Fees</h3>
              <div className="space-y-3">
                {paidFees.map((fee) => (
                  <div
                    key={fee.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{fee.feeType || 'Fee'}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {fee.academicYear} {fee.term ? `- ${fee.term}` : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600 dark:text-green-400">
                          Rs. {(fee.paidAmount || 0).toLocaleString('en-IN')}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Paid
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 mt-3">
                      <button
                        onClick={() => handleDownloadReceipt(fee)}
                        className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md flex items-center justify-center"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download Receipt
                      </button>
                      <button
                        onClick={() => handleSendEmail(fee)}
                        className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md flex items-center justify-center"
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Email Receipt
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BatchSchedulePage;
